// Area-wise plan creation.
//
// Same weekly grid as EquipmentPlanCreate, but every row is an area from the
// area_masters table instead of a machine from equipment_model. Area plans are
// stored in the same equipment_planner table with eqp_id holding the area_id --
// that is how the log side already links the two (see getEquipmentPlanningByArea).
//
// The area list is normalized on load to { equipment_id, equipment_name,
// eqp_sequence } so the grid, cascade and payload code below stays identical to
// the equipment planner.
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { DatePicker, Select, Input, Button, Modal, message, Spin, TimePicker, Dropdown, Alert, ConfigProvider } from 'antd';
import { DownOutlined, ExclamationCircleOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'antd/dist/reset.css';
import {
  fetchAreaPlannerAreaList as fetchAreaList,
  createAreaPlan as createEquipmentPlan,
  fetchAreaPlanner as fetchEqpPlanner,
  fetchAreaPlanVersions as fetchPlanVersions,
  publishAreaPlan as publishPlan,
  fetchAreaPlanHistory as fetchPlanHistory,
  fetchAvailableProducts,
  fetchProductVolumeOptions,
} from './api';
import useAreaPlanClipboard, { toExcludedSet, runBatched } from './areaPlan/useAreaPlanClipboard';
import { createDayFetcher as makeDayFetcher } from './areaPlan/cascadeFetch';
import AreaPlanPasteModal from './areaPlan/AreaPlanPasteModal';

const { TextArea } = Input;

// Hour grid constants - responsive to fill viewport (UI only)
const HOURS_PER_DAY = 24;
const TOTAL_DAYS = 7;
const TOTAL_HOURS = HOURS_PER_DAY * TOTAL_DAYS; // 168 hours
const AREA_LABEL_WIDTH = 170; // fixed width for area names (longer than equipment codes)

const AreaPlanCreate = () => {
  const [areaList, setAreaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Default to today
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingActivityId, setEditingActivityId] = useState(null); // Track which activity is being edited
  
  // Plan versions state
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Available products state
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Multi-delete mode state
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  
  // Reverse cascade delete mode state
  const [isReverseCascadeDeleteMode, setIsReverseCascadeDeleteMode] = useState(false);
  const [reverseCascadeEquipment, setReverseCascadeEquipment] = useState(null);
  const [selectedForReverseCascadeDelete, setSelectedForReverseCascadeDelete] = useState(new Set());
  const [deletingWithCascade, setDeletingWithCascade] = useState(false);

  // R&D filter mode state
  const [isRndFilterMode, setIsRndFilterMode] = useState(false);

  // Cut / copy / paste mode state. Selection locks to one area, the same way
  // reverse-cascade delete does -- "paste two areas into one area" has no
  // sensible answer.
  const [isClipboardMode, setIsClipboardMode] = useState(false);
  const [clipboardArea, setClipboardArea] = useState(null);
  const [selectedForClipboard, setSelectedForClipboard] = useState(new Set());
  // Pending paste: { planned, targetAreaId, targetAreaName, targetTime }
  const [pasteTarget, setPasteTarget] = useState(null);

  // Phone-width viewport: the activity form becomes a full-height sheet.
  // 576 matches the `max-width: 575px` breakpoint in the form's own CSS, so the
  // sheet and the stacked rows switch over together.
  const [isNarrowScreen, setIsNarrowScreen] = useState(
    typeof window !== 'undefined' && window.innerWidth < 576
  );
  useEffect(() => {
    const onResize = () => setIsNarrowScreen(window.innerWidth < 576);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // Helper: check if an activity is an R&D batch (product_name contains "R&D", case-insensitive)
  const isRndBatch = (activity) => {
    return activity?.product_name && /r\s*&\s*d|r\s*and\s*d/i.test(activity.product_name);
  };

  // Export the current week's area plan to CSV, grouped by area
  const handleExportAreaPlan = () => {
    if (activities.length === 0) {
      message.warning('No planned activities found for this week');
      return;
    }

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Group activities by area name
    const groups = {};
    activities.forEach(activity => {
      const areaName = activity.equipment_name || activity.equipment_id || 'Unknown';
      if (!groups[areaName]) groups[areaName] = [];
      groups[areaName].push(activity);
    });

    // Sort activities within each area by planned start time
    Object.values(groups).forEach(arr => {
      arr.sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf());
    });

    // Follow the on-screen area order, then append anything no longer in the master list
    const orderedAreas = allAreas.map(a => a.name).filter(name => groups[name]);
    Object.keys(groups).forEach(name => {
      if (!orderedAreas.includes(name)) orderedAreas.push(name);
    });

    const csvRows = [];

    // Header
    csvRows.push([`Area Plan - Week: ${weekRangeText}`].join(','));
    csvRows.push(['']);

    // Column headers
    csvRows.push([
      'Area', 'Activity Type', 'Product Name', 'Batch No.',
      'Planned Start', 'Planned End', 'Duration (hrs)', 'Remarks'
    ].map(escapeCsv).join(','));

    // Data rows grouped by area
    orderedAreas.forEach(areaName => {
      groups[areaName].forEach((activity, idx) => {
        const start = activity.start_time ? dayjs(activity.start_time) : null;
        const end = activity.end_time ? dayjs(activity.end_time) : null;
        const durationHrs =
          start && end && start.isValid() && end.isValid()
            ? Math.round(((end.valueOf() - start.valueOf()) / 3600000) * 10) / 10
            : '';

        csvRows.push([
          escapeCsv(idx === 0 ? areaName : ''),
          escapeCsv(activity.activity_type),
          escapeCsv(activity.product_name),
          escapeCsv(activity.batch_number),
          escapeCsv(start && start.isValid() ? start.format('DD-MMM-YYYY HH:mm') : ''),
          escapeCsv(end && end.isValid() ? end.format('DD-MMM-YYYY HH:mm') : ''),
          escapeCsv(durationHrs),
          escapeCsv(activity.remarks)
        ].join(','));
      });

      csvRows.push(['']); // blank row between areas
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileStart = weekStart.format('DD-MMM-YYYY');
    const fileEnd = weekEnd.format('DD-MMM-YYYY');
    link.download = `Area_Plan_${fileStart}_to_${fileEnd}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    message.success('Area plan exported successfully');
  };

  // Product volume options state
  const [volumeOptions, setVolumeOptions] = useState([]); // Raw options from API
  const [loadingVolumeOptions, setLoadingVolumeOptions] = useState(false);
  const [selectedVolumeOption, setSelectedVolumeOption] = useState(null);
  
  // Option mismatch warning state (when editing activity with outdated option)
  const [optionMismatchWarning, setOptionMismatchWarning] = useState(false);
  const [originalOptionSelected, setOriginalOptionSelected] = useState(null);
  
  // Helper function to determine equipment type category based on equipment name
  const getEquipmentCategory = useCallback((equipmentName) => {
    if (!equipmentName) return 'other';
    const name = equipmentName.toUpperCase().trim();
    
    // Granulation/FBP/Blending machines - use volume_gran_fbp_blend
    // Patterns: G1-RMG, G2-FBD, G2FBD, FBP-1A, FBP-2, B1, B-5, etc.
    if (
      /^G\d+-?(RMG|FBD)$/i.test(name) ||  // G1-RMG, G2-FBD, G3-RMG, etc.
      /^G\d+FBD$/i.test(name) ||           // G2FBD (without hyphen)
      /^FBP-?\d+[A-Z]?$/i.test(name) ||    // FBP-1A, FBP-1B, FBP-2, FBP-3, etc.
      /^B-?\d+$/i.test(name)               // B1, B2, B3, B-5, B-6, etc.
    ) {
      return 'granulation';
    }
    
    // Compression/Coating/Capsulation/Imprinting machines - use volume_comp_coat_cap_imp
    // Patterns: COMP-2, COAT-3, CAPS-5, IMP-2, etc.
    if (
      /^COMP-?\d+$/i.test(name) ||   // COMP-2, COMP-3, etc.
      /^COAT-?\d+$/i.test(name) ||   // COAT-2, COAT-3, etc.
      /^CAPS-?\d+$/i.test(name) ||   // CAPS-5, CAPS-6, etc.
      /^IMP-?\d+$/i.test(name)       // IMP-2, IMP-3, etc.
    ) {
      return 'compression';
    }

     // Packing machines
  if (
    /^P\d+\s*(PRIMARY|SECONDARY)$/i.test(name)
  ) {
    return 'packing';
  }
    
    return 'other';
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    equipment_id: null,
    equipment_name: '',
    activity_type: 'manufacturing',
    start_time: null,
    end_time: null,
    start_date: null, // Separate date field
    start_time_only: null, // Separate time field
    end_date: null, // Separate date field
    end_time_only: null, // Separate time field
    duration_hrs: '', // Duration in hours (auto-calculates end time)
    batch_number: '',
    batch_id: null, // ID from search-batch API
    product_name: '',
    volume_option: null, // Selected volume option value (for display in Select)
    volume_option_payload: null, // Original value for API payload (first option's value if combined)
    volume_option_data: null, // Full volume option data (id, num_batches, volumes, etc.)
    number_of_batches: 0, // Number of batches for auto-generation (0 = single batch mode)
    cleaning_type_between: null, // Cleaning type between batches
    cleaning_duration_between: '', // Cleaning duration in hours between batches
    cleaning_type: null,
    maintenance_type: null,
    remarks: '',
    // Manufacturing scheduling mode: 'auto' or 'manual'
    scheduling_mode: 'auto',
    // Manual batch entries - array of batch objects for manual scheduling
    manual_batches: [{
      batch_number: '',
      product_name: '',
      volume_option: null,
      volume_option_payload: null,
      volume_option_data: null,
      duration_hrs: '',
      cleaning_type: null,
      cleaning_duration_hrs: '',
      end_time: null
    }]
  });
  
  // Get the current equipment category for display purposes
  const currentEquipmentCategory = useMemo(() => {
    return getEquipmentCategory(formData.equipment_name);
  }, [formData.equipment_name, getEquipmentCategory]);
  
  // Process volume options based on equipment type - group by relevant volume field
  const processedVolumeOptions = useMemo(() => {
    // console.log('=== processedVolumeOptions useMemo ===', {
    //   volumeOptionsLength: volumeOptions?.length,
    //   equipment_name: formData.equipment_name
    // });
    
    if (!volumeOptions || volumeOptions.length === 0) return [];
    
    const equipmentCategory = getEquipmentCategory(formData.equipment_name);
    
    // Determine which volume field to group by (if known equipment type)
    const volumeField = equipmentCategory === 'granulation' 
      ? 'volume_gran_fbp_blend' 
      : equipmentCategory === 'compression' 
        ? 'volume_comp_coat_cap_imp' 
        : null;
    
    // Group options by base name (and volume value if equipment type is known)
    const groupedOptions = {};
    
    volumeOptions.forEach(option => {
      const volumeValue = volumeField ? option[volumeField] : 'default';
      
      // Extract base name and strength from label like "CB_Option1_Strength(25)" or "CB_Option1_Strength(7.5)"
      const match = option.label.match(/^(.+?)\(([\d.]+)\)$/);
      
      if (match) {
        const baseName = match[1];
        const strength = match[2];
        // For known equipment types, group by baseName + volumeValue
        // For unknown equipment ('other'), group just by baseName
        const groupKey = volumeField ? `${baseName}_${volumeValue}` : baseName;
        
        if (!groupedOptions[groupKey]) {
          groupedOptions[groupKey] = {
            baseName,
            volumeValue,
            strengths: [],
            options: [],
            firstOption: option
          };
        }
        groupedOptions[groupKey].strengths.push(strength);
        groupedOptions[groupKey].options.push(option);
      } else {
        // No pattern match - add as single option with unique key
        const uniqueKey = `single_${option.value}`;
        groupedOptions[uniqueKey] = {
          baseName: option.label,
          volumeValue,
          strengths: [],
          options: [option],
          firstOption: option,
          isSingle: true
        };
      }
    });
    
    // Create final options array
    const result = [];
    
    Object.values(groupedOptions).forEach(group => {
      if (group.isSingle || group.strengths.length === 1) {
        // Single option - use as-is
        result.push(group.firstOption);
      } else {
        // Multiple strengths with same volume - combine into single option
        const sortedStrengths = group.strengths.sort((a, b) => Number(a) - Number(b));
        const combinedLabel = `${group.baseName}(${sortedStrengths.join(',')})`;
        
        // Create combined option - store original first option's value for payload
        result.push({
          ...group.firstOption,
          label: combinedLabel,
          value: combinedLabel,
          originalValue: group.firstOption.value, // Use first option's value for API payload
          originalOptions: group.options,
          combinedStrengths: sortedStrengths
        });
      }
    });
    
    // Sort by base name first, then by first strength value numerically
    return result.sort((a, b) => {
      // Extract base name and first strength for comparison
      const matchA = a.label.match(/^(.+?)\(([\d.]+)/);
      const matchB = b.label.match(/^(.+?)\(([\d.]+)/);
      
      const baseNameA = matchA ? matchA[1] : a.label;
      const baseNameB = matchB ? matchB[1] : b.label;
      
      // First sort by base name alphabetically
      const nameCompare = baseNameA.localeCompare(baseNameB);
      if (nameCompare !== 0) return nameCompare;
      
      // Then sort by first strength value numerically
      const strengthA = matchA ? parseFloat(matchA[2]) : 0;
      const strengthB = matchB ? parseFloat(matchB[2]) : 0;
      return strengthA - strengthB;
    });
  }, [volumeOptions, formData.equipment_name, getEquipmentCategory]);
  
  // Effect to match option_selected with processedVolumeOptions when editing
  useEffect(() => {
    // console.log('=== Option Matching Effect ===' , {
    //   editingActivityId,
    //   processedVolumeOptionsLength: processedVolumeOptions.length,
    //   volume_option_payload: formData.volume_option_payload,
    //   volume_option: formData.volume_option,
    //   processedVolumeOptions: processedVolumeOptions.map(o => ({ value: o.value, label: o.label, originalValue: o.originalValue }))
    // });
    
    // Only run when editing and we have options and a stored option_selected
    if (!editingActivityId || processedVolumeOptions.length === 0 || !formData.volume_option_payload) {
      return;
    }
    
    // Don't re-match if already matched
    if (formData.volume_option) {
      return;
    }
    
    const optionSelected = formData.volume_option_payload;
    let matchingProcessedOption = null;
    
    // Search through processedVolumeOptions for a match
    for (const processedOpt of processedVolumeOptions) {
      // Check direct value match
      if (processedOpt.value === optionSelected || processedOpt.label === optionSelected) {
        matchingProcessedOption = processedOpt;
        break;
      }
      
      // Check originalValue (for combined options)
      if (processedOpt.originalValue === optionSelected) {
        matchingProcessedOption = processedOpt;
        break;
      }
      
      // Check originalOptions array (for combined options with multiple strengths)
      if (processedOpt.originalOptions && processedOpt.originalOptions.length > 0) {
        const foundInOriginals = processedOpt.originalOptions.some(
          origOpt => origOpt.value === optionSelected || origOpt.label === optionSelected
        );
        if (foundInOriginals) {
          matchingProcessedOption = processedOpt;
          break;
        }
      }
    }
    
    if (matchingProcessedOption) {
      // Found a match - set the volume option using the processed option's value
      setFormData(prev => ({
        ...prev,
        volume_option: matchingProcessedOption.value,
        volume_option_data: matchingProcessedOption
      }));
      setSelectedVolumeOption(matchingProcessedOption);
      // Clear any existing mismatch warning
      setOptionMismatchWarning(false);
      setOriginalOptionSelected(null);
    } else {
      // No match found - show warning
      setOptionMismatchWarning(true);
      setOriginalOptionSelected(optionSelected);
    }
  }, [editingActivityId, processedVolumeOptions, formData.volume_option_payload, formData.volume_option]);
  
  // Refs for scroll sync
  const headerScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const fixedScrollRef = useRef(null);
  
  // Sync horizontal scroll
  const handleContentScroll = (e) => {
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    if (fixedScrollRef.current) fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
  };
  
  const handleHeaderScroll = (e) => {
    if (contentScrollRef.current) contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    if (fixedScrollRef.current) fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
  };
  
  const handleFixedScroll = (e) => {
    if (contentScrollRef.current) contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  // Activity type options
  const activityTypes = [
    { value: 'manufacturing', label: 'Manufacturing', color: '#3b82f6' },
    { value: 'cleaning', label: 'Cleaning', color: '#10b981' },
    { value: 'maintenance', label: 'Maintenance', color: '#f59e0b' }
  ];

  const cleaningTypes = [
    { value: 'Type-A', label: 'Type-A' },
    { value: 'Type-B', label: 'Type-B' },
    { value: 'Type-C', label: 'Type-C' }
  ];

  const maintenanceTypes = [
    // { value: 'Preventive', label: 'Preventive Maintenance' },
    // { value: 'Corrective', label: 'Corrective Maintenance' },
    { value: 'PMP', label: 'PMP' },
    { value: 'Breakdown', label: 'Breakdown' },
    // { value: 'Scheduled', label: 'Scheduled Maintenance' },
    // { value: 'Calibration', label: 'Calibration' },
    // { value: 'Other', label: 'Other' }
  ];

  // Fetch the area master list on mount.
  // Normalized to the equipment shape the grid below already understands:
  // area_id -> equipment_id, area_name -> equipment_name, area_sequence -> eqp_sequence.
  useEffect(() => {
    const loadAreaList = async () => {
      try {
        setLoading(true);
        const response = await fetchAreaList();
        const areas = (response.data || []).map(area => ({
          id: area.id,
          equipment_id: area.area_id,
          // Label the row with the plant's own name for the room - location, from
          // equipment_models via area_masters (G1, COMP-1, 'FBP-1A, FBP-1B'). Shorter
          // and more recognisable on the grid than area_name. The four rooms with no
          // machine (Sifting, Milling, Band Sealing) have no location and fall back.
          equipment_name: area.location || area.area_name || area.area_label || area.area_id,
          area_name: area.area_name || null,
          eqp_sequence: area.area_sequence,
        }));
        setAreaList(areas);
      } catch (err) {
        console.error('Error loading area list:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAreaList();
  }, []);

  // Fetch plan versions on mount
  useEffect(() => {
    const loadPlanVersions = async () => {
      try {
        setLoadingVersions(true);
        const response = await fetchPlanVersions();
        setPlanVersions(response.data || []);
        // Set the latest version as default (first item is the latest)
        if (response.data && response.data.length > 0) {
          setSelectedVersion(response.data[0].plan_version);
        }
      } catch (err) {
        console.error('Error loading plan versions:', err);
      } finally {
        setLoadingVersions(false);
      }
    };
    loadPlanVersions();
  }, []);

  // Fetch available products on mount
  useEffect(() => {
    const loadAvailableProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await fetchAvailableProducts();
        setAvailableProducts(response.data || []);
      } catch (err) {
        console.error('Error loading available products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadAvailableProducts();
  }, []);

  // Calculate week start (Monday) and end (Sunday) based on selected date
  // Safari-safe: Wrap in try-catch to prevent rendering errors
  const weekStart = useMemo(() => {
    try {
      // Ensure selectedDate is valid
      const validDate = dayjs(selectedDate).isValid() ? selectedDate : dayjs();
      const start = validDate.startOf('week'); // Monday
      return start;
    } catch (error) {
      console.error('[Safari] Error calculating weekStart:', error);
      return dayjs().startOf('week');
    }
  }, [selectedDate]);

  const weekEnd = useMemo(() => {
    try {
      return weekStart.endOf('week'); // Sunday
    } catch (error) {
      console.error('[Safari] Error calculating weekEnd:', error);
      return dayjs().endOf('week');
    }
  }, [weekStart]);

  // Check if selected version is the latest version
  const isLatestVersion = useMemo(() => {
    if (!selectedVersion || planVersions.length === 0) return true;
    // Latest version is the first one in the sorted list (highest version number)
    return selectedVersion === planVersions[0]?.plan_version;
  }, [selectedVersion, planVersions]);

  // Load activities for the week and next week to handle cross-week dependencies
  // Safari-safe: Comprehensive error handling to prevent component crash
  const loadActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      // Fetch activities for current week AND next week (14 days total) using the new API
      // This ensures we catch dependent activities even if main activity spans to next week
      const allWeekActivities = [];
      
      // If viewing an older version, use plan-history API with date range
      if (!isLatestVersion && selectedVersion) {
        const startDate = weekStart.format('YYYY-MM-DD');
        const endDate = weekStart.add(13, 'day').format('YYYY-MM-DD'); // 14 days total
        try {
          const response = await fetchPlanHistory({
            version: selectedVersion,
            start_date: startDate,
            end_date: endDate
          });
          
          let activitiesData = response.data || [];
          
          if (activitiesData.length > 0) {
            // Transform the API response to match the expected format
            const transformedActivities = activitiesData.map(item => {
              // Determine activity type based on eqp_plan_type
              let activityType = item.activity_type;
              if (!activityType) {
                if (item.eqp_plan_type === 'Operation') {
                  activityType = 'manufacturing';
                } else if (item.eqp_plan_type === 'Cleaning') {
                  activityType = 'cleaning';
                } else {
                  activityType = 'maintenance';
                }
              }
              
              if (activityType) {
                activityType = String(activityType).toLowerCase();
                if (activityType === 'operation') {
                  activityType = 'manufacturing';
                }
              } else {
                activityType = 'maintenance';
              }
              
              const eqpId = item.eqp_id || item.equipment_id;
              const equipmentName = item.equipment_name || eqpId;
              
              return {
                id: item.id,
                original_id: item.original_id,
                equipment_id: eqpId,
                equipment_name: equipmentName,
                eqp_id: eqpId,
                activity_type: activityType,
                batch_number: item.product_batch || item.batch_number || null,
                product_name: item.product_name || '',
                cleaning_type: activityType === 'cleaning' && item.eqp_activity ? item.eqp_activity : (item.cleaning_type || null),
                maintenance_type: activityType === 'maintenance' && item.eqp_activity ? item.eqp_activity : (item.maintenance_type || null),
                start_time: item.start_date || item.start_time,
                end_time: item.end_date || item.end_time,
                remarks: item.remarks || '',
                plan_version: item.plan_version,
                published_at: item.published_at,
                option_selected: item.option_selected || ''
              };
            });
            allWeekActivities.push(...transformedActivities);
          }
        } catch (err) {
          console.error(`Error loading plan history for version ${selectedVersion}:`, err);
        }
      } else {
        // Use the regular API for latest version (day by day)
        // 14 days (this week + next, for activities that span the boundary).
        //
        // These are fetched all at once. This used to be 14 sequential awaits,
        // which measured ~9.6s against a local server: every request re-queries
        // the same 62-area master list, so about half of each call was repeated
        // work and it was paid 14 times over. The same refresh in parallel
        // measures ~0.9s. loadActivities runs after every save, delete and
        // paste, so this was the bulk of the wait the planner felt.
        const dayResponses = await Promise.all(
          Array.from({ length: 14 }, (_, i) => {
            const dateStr = weekStart.add(i, 'day').format('YYYY-MM-DD');
            // A failed day stays isolated and does not sink the whole refresh,
            // exactly as the per-day try/catch did before.
            return fetchEqpPlanner(dateStr)
              .then((response) => ({ dateStr, response }))
              .catch((err) => {
                console.error(`Error loading activities for ${dateStr}:`, err);
                return { dateStr, response: null };
              });
          })
        );

        for (const { dateStr, response } of dayResponses) {
          try {
          // Reduced logging - only log if there's an issue or in development
          if (process.env.NODE_ENV === 'development' && response?.activities?.length > 0) {
            //console.log(`API Response for ${dateStr}: ${response.activities.length} activities`);
          }
          
          // Handle different response formats - API returns 'activities' not 'data'
          let activitiesData = [];
          
          // Direct check - prioritize response.activities (this is what the API returns)
          if (response && response.activities) {
            if (Array.isArray(response.activities)) {
              activitiesData = response.activities;
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.warn(`response.activities exists but is not an array:`, typeof response.activities);
              }
            }
          } else if (response && response.data && Array.isArray(response.data)) {
            activitiesData = response.data;
          } else if (Array.isArray(response)) {
            activitiesData = response;
          } else {
            // Only log warnings in development
            if (process.env.NODE_ENV === 'development') {
              console.warn(`⚠ No activities found in response for ${dateStr}`);
            }
          }
          
          if (activitiesData.length > 0) {
            // Transform the API response to match the expected format
            const transformedActivities = activitiesData.map(item => {
              // Removed verbose logging
              
              // Use activity_type from API if available, otherwise determine from eqp_plan_type
              let activityType = item.activity_type;
              if (!activityType) {
                // Fallback: Determine activity type based on eqp_plan_type
                if (item.eqp_plan_type === 'Operation') {
                  activityType = 'manufacturing';
                } else if (item.eqp_plan_type === 'Cleaning') {
                  // If it has eqp_activity, it's cleaning, otherwise it might be maintenance
                  activityType = item.eqp_activity ? 'cleaning' : 'maintenance';
                } else {
                  activityType = 'maintenance';
                }
              }
              
              // Normalize activity_type to match expected values (lowercase)
              if (activityType) {
                activityType = String(activityType).toLowerCase();
                if (activityType === 'operation') {
                  activityType = 'manufacturing';
                }
              } else {
                activityType = 'maintenance';
              }
              
              // Removed verbose activity type logging

              // Use eqp_id as the primary identifier (this is what the API returns)
              // Map it to both equipment_id and equipment_name for flexible matching
              const eqpId = item.eqp_id || item.equipment_id;
              const equipmentName = item.equipment_name || eqpId;
              
              // Normalize date strings for consistent parsing across browsers
              // Safari is stricter about date formats, so we ensure dates are in a standard format
              // Fix the +0530 timezone format that Safari has trouble with
              const normalizeDate = (dateStr) => {
                if (!dateStr) return null;
                // Handle timezone offset format that Safari doesn't parse well: "2026-01-12 00:00:00.000 +0530"
                if (typeof dateStr === 'string' && dateStr.includes(' +') && dateStr.match(/\+\d{4}$/)) {
                  // Convert "YYYY-MM-DD HH:mm:ss.SSS +0530" to ISO format with timezone
                  // Extract the timezone offset
                  const tzMatch = dateStr.match(/([+-])(\d{2})(\d{2})$/);
                  if (tzMatch) {
                    const [, sign, hours, minutes] = tzMatch;
                    // Convert to ISO format: "YYYY-MM-DDTHH:mm:ss.SSS+HH:MM"
                    const datePart = dateStr.replace(/\s*[+-]\d{4}$/, '').replace(' ', 'T');
                    return `${datePart}${sign}${hours}:${minutes}`;
                  }
                }
                // Validate that dayjs can parse it
                const parsed = dayjs(dateStr);
                if (!parsed.isValid()) {
                  // Only log in development
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('Date string may not parse correctly:', dateStr);
                  }
                  // Try to return a more standard format if possible
                  if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return `${dateStr}T00:00:00`;
                  }
                }
                // Return original string - dayjs will handle parsing when needed
                return dateStr;
              };
              
              const transformed = {
                id: item.id,
                equipment_id: eqpId,
                equipment_name: equipmentName, // Try to match with equipment list
                // Also store eqp_id separately for matching
                eqp_id: eqpId,
                activity_type: activityType,
                batch_number: item.batch_number || (item.dispensing_request_id && item.dispensing_request_id !== '' ? item.dispensing_request_id : null),
                product_name: item.product_name || '', // Include product_name from API
                cleaning_type: activityType === 'cleaning' && item.eqp_activity ? item.eqp_activity : (item.cleaning_type || null),
                maintenance_type: activityType === 'maintenance' && item.eqp_activity ? item.eqp_activity : (item.maintenance_type || null),
                start_time: normalizeDate(item.start_date || item.start_time),
                end_time: normalizeDate(item.end_date || item.end_time),
                remarks: item.remarks || '', // Include remarks from API
                option_selected: item.option_selected || '' // Include option_selected from API
              };
              
              return transformed;
            });
            allWeekActivities.push(...transformedActivities);
          }
        } catch (err) {
          console.error(`Error loading activities for ${dateStr}:`, err);
        }
      }
      } // End of else block for latest version
      // Remove duplicate activities based on ID (same activity might be returned for multiple days)
      const uniqueActivities = [];
      const seenIds = new Set();
      for (const activity of allWeekActivities) {
        if (activity.id && !seenIds.has(activity.id)) {
          seenIds.add(activity.id);
          uniqueActivities.push(activity);
        } else if (!activity.id) {
          // If no ID, use a combination of equipment_id, start_time, and end_time as unique key
          const uniqueKey = `${activity.equipment_id || activity.equipment_name || ''}_${activity.start_time}_${activity.end_time}`;
          if (!seenIds.has(uniqueKey)) {
            seenIds.add(uniqueKey);
            uniqueActivities.push(activity);
          }
        }
      }
      
      // Reduced logging - only log summary in development
      if (process.env.NODE_ENV === 'development' && uniqueActivities.length > 0) {
        //console.log(`Loaded ${uniqueActivities.length} unique activities for the week`);
      }
      setActivities(uniqueActivities);
      return uniqueActivities; // Return fresh data for callers that need it synchronously
    } catch (err) {
      console.error('Error loading activities:', err);
      setActivities([]);
      return []; // Return empty array on error
    } finally {
      setLoadingActivities(false);
    }
  }, [weekStart, selectedVersion, isLatestVersion]);

  // Load activities when date changes or version changes
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Calculate display range for the week
  // Use dayjs to ensure consistent date handling across browsers
  // Safari-safe: Wrap in try-catch to prevent rendering errors
  const displayStartTime = useMemo(() => {
    try {
      const start = weekStart.startOf('day');
      // Removed verbose debug logging
      return start.toDate();
    } catch (error) {
      console.error('[Safari] Error calculating displayStartTime:', error);
      return dayjs().startOf('week').startOf('day').toDate();
    }
  }, [weekStart]);

  const displayEndTime = useMemo(() => {
    try {
      const end = weekEnd.endOf('day');
      // Removed verbose debug logging
      return end.toDate();
    } catch (error) {
      console.error('[Safari] Error calculating displayEndTime:', error);
      return dayjs().endOf('week').endOf('day').toDate();
    }
  }, [weekEnd]);

  // Calculate activity position and width for each day block
  const getActivityBlocks = useCallback((equipmentIdentifier) => {
    const startTime = displayStartTime;
    const endTime = displayEndTime;

    const filtered = activities.filter(activity => {
      // Match by equipment_name, equipment_id, or eqp_id (with string conversion for safety)
      const matchesEquipment = 
        String(activity.equipment_name || '') === String(equipmentIdentifier || '') || 
        String(activity.equipment_id || '') === String(equipmentIdentifier || '') ||
        String(activity.eqp_id || '') === String(equipmentIdentifier || '');
      
      if (!matchesEquipment) {
        return false;
      }
      
      try {
        // Use dayjs for consistent date parsing across browsers (Safari is stricter)
        // Parse dates more robustly
        let activityStart, activityEnd;
        
        // Parse start time - use multiple parsing strategies for Safari compatibility
        if (activity.start_time) {
          // Try dayjs first (most reliable)
          let parsedStart = dayjs(activity.start_time);
          if (parsedStart.isValid()) {
            activityStart = parsedStart.toDate();
          } else {
            // Try parsing with explicit format strings that Safari accepts
            const formats = [
              'YYYY-MM-DD HH:mm:ss',
              'YYYY-MM-DDTHH:mm:ss',
              'YYYY-MM-DD HH:mm:ss.SSS',
              'YYYY-MM-DDTHH:mm:ss.SSS',
              'YYYY-MM-DDTHH:mm:ssZ',
              'YYYY-MM-DD'
            ];
            
            let parsed = null;
            for (const format of formats) {
              parsed = dayjs(activity.start_time, format, true);
              if (parsed.isValid()) {
                activityStart = parsed.toDate();
                break;
              }
            }
            
            // Last resort: native Date (Safari might handle it)
            if (!parsed || !parsed.isValid()) {
              activityStart = new Date(activity.start_time);
              if (isNaN(activityStart.getTime())) {
                console.warn('[Safari Debug] Invalid start_time:', activity.start_time, activity);
                return false;
              }
            }
          }
        } else {
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Debug] Missing start_time:', activity);
          }
          return false;
        }
        
        // Parse end time - use multiple parsing strategies for Safari compatibility
        if (activity.end_time) {
          // Try dayjs first (most reliable)
          let parsedEnd = dayjs(activity.end_time);
          if (parsedEnd.isValid()) {
            activityEnd = parsedEnd.toDate();
          } else {
            // Try parsing with explicit format strings that Safari accepts
            const formats = [
              'YYYY-MM-DD HH:mm:ss',
              'YYYY-MM-DDTHH:mm:ss',
              'YYYY-MM-DD HH:mm:ss.SSS',
              'YYYY-MM-DDTHH:mm:ss.SSS',
              'YYYY-MM-DDTHH:mm:ssZ',
              'YYYY-MM-DD'
            ];
            
            let parsed = null;
            for (const format of formats) {
              parsed = dayjs(activity.end_time, format, true);
              if (parsed.isValid()) {
                activityEnd = parsed.toDate();
                break;
              }
            }
            
            // Last resort: native Date (Safari might handle it)
            if (!parsed || !parsed.isValid()) {
              activityEnd = new Date(activity.end_time);
              if (isNaN(activityEnd.getTime())) {
                activityEnd = endTime; // Use endTime as fallback
              }
            }
          }
        } else {
          activityEnd = endTime; // Use endTime if no end_time
        }
        
        // Check if activity overlaps with display range
        // Activity is in range if: activityStart <= displayEndTime AND activityEnd >= displayStartTime
        // Use <= and >= to include activities exactly on boundaries (important for Safari)
        const isInRange = activityStart <= endTime && activityEnd >= startTime;
        
        // Only log filtered out activities in development mode
        if (process.env.NODE_ENV === 'development' && !isInRange && activityStart) {
          // console.log(`[Debug] Activity filtered out [${activity.equipment_name}]:`, {
          //   originalStartTime: activity.start_time,
          //   parsedStart: activityStart.toISOString(),
          //   displayRange: `${startTime.toISOString()} to ${endTime.toISOString()}`
          // });
        }
        
        return isInRange;
      } catch (e) {
        console.error('Error parsing activity dates:', e, activity);
        return false;
      }
    });
    
    // Removed verbose logging - only log if there are issues in development
    // if (process.env.NODE_ENV === 'development' && activities.length > 0 && filtered.length === 0) {
    //   //console.log(`[Debug] No activities found for ${equipmentIdentifier} out of ${activities.length} total`);
    // }
    
    // Group activities by day and calculate positions
    const dayWidth = 100 / 7; // Each day is ~14.28% of the week
    
    // Week start time in ms for hour-based percentage calculations
    const weekStartMs = startTime.getTime();
    const msPerHour = 60 * 60 * 1000;
    
    return filtered
      .map(activity => {
        // Use dayjs for consistent date parsing across browsers (Safari is stricter)
        let activityStart, activityEnd;
        
        // Parse start time with robust error handling
        if (activity.start_time) {
          const parsedStart = dayjs(activity.start_time);
          activityStart = parsedStart.isValid() ? parsedStart.toDate() : new Date(activity.start_time);
          if (isNaN(activityStart.getTime())) {
            console.error('Invalid activity start_time:', activity.start_time, activity);
            return null; // Skip this activity
          }
        } else {
          console.error('Missing activity start_time:', activity);
          return null; // Skip this activity
        }
        
        // Parse end time with robust error handling
        if (activity.end_time) {
          const parsedEnd = dayjs(activity.end_time);
          activityEnd = parsedEnd.isValid() ? parsedEnd.toDate() : new Date(activity.end_time);
          if (isNaN(activityEnd.getTime())) {
            activityEnd = endTime; // Use endTime as fallback
          }
        } else {
          activityEnd = endTime; // Use endTime if no end_time
        }
        
        // Calculate which day index (0-6) the activity starts and ends on
        const daysFromStart = (activityStart.getTime() - startTime.getTime()) / (24 * 60 * 60 * 1000);
        const daysFromEnd = (activityEnd.getTime() - startTime.getTime()) / (24 * 60 * 60 * 1000);
        const startDayIndex = Math.floor(Math.max(0, Math.min(6, daysFromStart)));
        const endDayIndex = Math.floor(Math.max(0, Math.min(6, daysFromEnd)));
        
        // Position card on the start day
        const dayIndex = startDayIndex;
        
        // Calculate how many days the activity spans
        const spanDays = Math.max(1, endDayIndex - startDayIndex + 1);
        
        // Calculate position within the day based on actual time
        // Get the start of the start day using dayjs for consistency
        // Use the same timezone as the activity dates to avoid timezone shifts
        const dayStart = dayjs(startTime).add(startDayIndex, 'day').startOf('day').toDate();
        
        // Calculate position within the day (0-100% of the day)
        const dayStartMs = dayStart.getTime();
        const activityStartMs = activityStart.getTime();
        const activityEndMs = activityEnd.getTime();
        
        // Position within the start day (percentage of day width)
        const positionInDay = Math.max(0, Math.min(100, ((activityStartMs - dayStartMs) / (24 * 60 * 60 * 1000)) * 100));
        
        // Calculate width based on actual duration
        const totalDurationMs = activityEndMs - activityStartMs;
        const widthInDays = totalDurationMs / (24 * 60 * 60 * 1000);
        
        // Hour-based percentage positioning (more precise than day-based)
        const clampedStart = Math.max(activityStartMs, weekStartMs);
        const clampedEnd = Math.min(activityEndMs, endTime.getTime());
        const hoursFromStart = (clampedStart - weekStartMs) / msPerHour;
        const durationHours = (clampedEnd - clampedStart) / msPerHour;
        
        // Percentage positioning relative to total grid width
        const leftPercent = (hoursFromStart / TOTAL_HOURS) * 100;
        const rawWidthPercent = (durationHours / TOTAL_HOURS) * 100;
        // Minimum bar width of 0.3% and small gap to prevent adjacent bars from touching
        const minBarWidthPercent = 0.3;
        const gapPercent = 0.1;
        const widthPercent = Math.max(minBarWidthPercent, rawWidthPercent - gapPercent);
        
        return {
          ...activity,
          dayIndex,
          leftPercent,
          widthPercent,
          activityStart,
          activityEnd
        };
      })
      .sort((a, b) => {
        // Sort by day index first, then by start time
        if (a.dayIndex !== b.dayIndex) {
          return a.dayIndex - b.dayIndex;
        }
        return a.activityStart.getTime() - b.activityStart.getTime();
      })
      .reduce((results, activity) => {
        // Calculate vertical stacking for overlapping activities
        let rowIndex = 0;
        const currStart = activity.activityStart.getTime();
        const currEnd = activity.activityEnd.getTime();
        
        for (let i = 0; i < results.length; i++) {
          const prev = results[i];
          // Check if activities overlap in time (not just same day, but actual time overlap)
          const prevStart = prev.activityStart.getTime();
          const prevEnd = prev.activityEnd.getTime();
          
          // If they overlap in time (current starts before previous ends AND current ends after previous starts), stack them
          if (currStart < prevEnd && currEnd > prevStart) {
            rowIndex = Math.max(rowIndex, prev.rowIndex + 1);
          }
        }
        
        results.push({ ...activity, rowIndex });
        return results;
      }, []);
  }, [activities, displayStartTime, displayEndTime]);

  // Activity type colors
  const activityColors = {
    manufacturing: '#3b82f6',
    cleaning: '#10b981',
    maintenance: '#f59e0b',
  };

  // Area list sorted dynamically from API using the area sequence
  const allAreas = useMemo(() => {
    return areaList
      .map(eq => {
        // Extract eqp_sequence from API response if available
        const eqpSequence = eq.eqp_sequence !== undefined && eq.eqp_sequence !== null 
          ? Number(eq.eqp_sequence) 
          : null;
        
        return { 
          id: eq.equipment_id || eq.equipment_name || eq.id, // Use equipment_id first (e.g., PRE007), fallback to equipment_name or id
          name: eq.equipment_name || eq.name || eq.equipment_id || eq.id,
          equipmentId: eq.equipment_id || eq.equipment_name || eq.id, // Use equipment_id from equipment list (e.g., PRE007) for API calls
          eqp_sequence: eqpSequence, // Preserve sequence from API
          areaName: eq.area_name || null // full room name, shown on hover
        };
      })
      .sort((a, b) => {
        // Sort by eqp_sequence if available (API-defined order)
        if (a.eqp_sequence !== null && b.eqp_sequence !== null) {
          return a.eqp_sequence - b.eqp_sequence;
        }
        // If only one has eqp_sequence, prioritize it
        if (a.eqp_sequence !== null) return -1;
        if (b.eqp_sequence !== null) return 1;
        // If neither has eqp_sequence, sort alphabetically with numeric support (natural sort)
        const nameA = a?.name != null ? String(a.name) : '';
        const nameB = b?.name != null ? String(b.name) : '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [areaList]);

  // Generate 7 days array (Monday to Sunday)
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const day = weekStart.add(i, 'day');
      return {
        index: i,
        date: day,
        dayName: day.format('ddd'),
        dayNumber: day.format('DD'),
        month: day.format('MMM'),
        fullDate: day.format('YYYY-MM-DD')
      };
    });
  }, [weekStart]);

  // Handle cell click - open modal
  const handleCellClick = (equipment, day) => {
    // Create time based on clicked day at 00:00
    const cellTime = day.date.startOf('day');
    
    setSelectedCell({ equipment, day, cellTime: cellTime.toDate() });
    
    // Find the last activity for the same equipment on the same day
    const equipmentIdentifier = equipment.id || equipment.name;
    const dayStart = day.date.startOf('day').toDate();
    const dayEnd = day.date.endOf('day').toDate();
    
    // Filter activities for the same equipment on the same day
    const sameDayActivities = activities.filter(activity => {
      const matchesEquipment = 
        String(activity.equipment_name || '') === String(equipmentIdentifier || '') || 
        String(activity.equipment_id || '') === String(equipmentIdentifier || '') ||
        String(activity.eqp_id || '') === String(equipmentIdentifier || '');
      
      if (!matchesEquipment) return false;
      
      try {
        // Use dayjs for consistent date parsing across browsers (Safari is stricter)
        const activityStart = dayjs(activity.start_time).isValid() 
          ? dayjs(activity.start_time).toDate() 
          : new Date(activity.start_time);
        const activityEnd = activity.end_time 
          ? (dayjs(activity.end_time).isValid() ? dayjs(activity.end_time).toDate() : new Date(activity.end_time))
          : dayEnd;
        // Check if activity is on the same day
        return activityStart <= dayEnd && activityEnd >= dayStart;
      } catch (e) {
        return false;
      }
    });
    
    // Sort by end_time and get the last activity's end time
    let defaultStartTime = cellTime;
    if (sameDayActivities.length > 0) {
      const sortedActivities = sameDayActivities.sort((a, b) => {
        // Use dayjs for consistent date parsing across browsers (Safari is stricter)
        const endA = a.end_time 
          ? (dayjs(a.end_time).isValid() ? dayjs(a.end_time).toDate().getTime() : new Date(a.end_time).getTime())
          : 0;
        const endB = b.end_time 
          ? (dayjs(b.end_time).isValid() ? dayjs(b.end_time).toDate().getTime() : new Date(b.end_time).getTime())
          : 0;
        return endB - endA; // Sort descending to get the latest end time
      });
      
      const lastActivity = sortedActivities[0];
      if (lastActivity.end_time) {
        defaultStartTime = dayjs(lastActivity.end_time);
        //console.log(`Found last activity ending at ${defaultStartTime.format('DD-MMM-YYYY HH:mm')}, using as start time`);
      }
    }
    
    const startDate = defaultStartTime;

    // Holding a cut or copy? A cell click is a paste, not a new activity. The
    // default target is the same "after whatever is already there" time the
    // create form uses, which is round because plan times always are.
    if (isHolding) {
      openPasteConfirm(equipment.equipmentId || equipment.id, startDate);
      return;
    }

    setFormData({
      equipment_id: equipment.equipmentId || equipment.id, // Use the actual equipment ID
      equipment_name: equipment.name,
      activity_type: 'manufacturing',
      start_time: startDate,
      end_time: null, // Empty by default
      start_date: startDate,
      start_time_only: startDate,
        end_date: null,
        end_time_only: null,
        duration_hrs: '',
        batch_number: '',
        batch_id: null,
      product_name: '',
      number_of_batches: 0,
      cleaning_type_between: null,
      cleaning_duration_between: '',
      cleaning_type: null,
      maintenance_type: null,
      remarks: '',
      scheduling_mode: 'auto',
      manual_batches: [{
        batch_number: '',
        product_name: '',
        duration_hrs: '',
        cleaning_type: null,
        cleaning_duration_hrs: '',
        end_time: null
      }]
    });
    setIsModalOpen(true);
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Combine start_date and start_time_only into start_time
      if (field === 'start_date' || field === 'start_time_only') {
        if (updated.start_date && updated.start_time_only) {
          // Combine date and time
          const date = dayjs.isDayjs(updated.start_date) ? updated.start_date : dayjs(updated.start_date);
          const time = dayjs.isDayjs(updated.start_time_only) ? updated.start_time_only : dayjs(updated.start_time_only);
          if (date.isValid() && time.isValid()) {
            updated.start_time = date
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
          }
        } else if (updated.start_date && !updated.start_time_only) {
          // If only date is set, use start of day
          const date = dayjs.isDayjs(updated.start_date) ? updated.start_date : dayjs(updated.start_date);
          updated.start_time = date.isValid() ? date.startOf('day') : null;
        } else if (!updated.start_date && updated.start_time_only && updated.start_time) {
          // If only time is set, use existing start_time date with new time
          const time = dayjs.isDayjs(updated.start_time_only) ? updated.start_time_only : dayjs(updated.start_time_only);
          const currentDate = dayjs.isDayjs(updated.start_time) ? updated.start_time : dayjs(updated.start_time);
          if (time.isValid() && currentDate.isValid()) {
            updated.start_time = currentDate
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
            // Also update start_date to match
            updated.start_date = updated.start_time;
          }
        }
      }
      
      // Combine end_date and end_time_only into end_time
      if (field === 'end_date' || field === 'end_time_only') {
        if (updated.end_date && updated.end_time_only) {
          // Combine date and time
          const date = dayjs.isDayjs(updated.end_date) ? updated.end_date : dayjs(updated.end_date);
          const time = dayjs.isDayjs(updated.end_time_only) ? updated.end_time_only : dayjs(updated.end_time_only);
          if (date.isValid() && time.isValid()) {
            updated.end_time = date
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
          }
        } else if (updated.end_date && !updated.end_time_only) {
          // If only date is set, use end of day
          const date = dayjs.isDayjs(updated.end_date) ? updated.end_date : dayjs(updated.end_date);
          updated.end_time = date.isValid() ? date.endOf('day') : null;
        } else if (!updated.end_date && updated.end_time_only && updated.end_time) {
          // If only time is set, use existing end_time date with new time
          const time = dayjs.isDayjs(updated.end_time_only) ? updated.end_time_only : dayjs(updated.end_time_only);
          const currentDate = dayjs.isDayjs(updated.end_time) ? updated.end_time : dayjs(updated.end_time);
          if (time.isValid() && currentDate.isValid()) {
            updated.end_time = currentDate
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
            // Also update end_date to match
            updated.end_date = updated.end_time;
          }
        }
      }
      
      return updated;
    });
  };

  // Auto-calculate end time from start_time + duration_hrs (in hours)
  const recalcEndFromDuration = useCallback((nextDurationHrs, baseStartTime) => {
    const duration = Number(nextDurationHrs);
    if (!baseStartTime || !dayjs(baseStartTime).isValid()) return;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const start = dayjs(baseStartTime);
    const end = start.add(duration, 'hour');

    setFormData(prev => ({
      ...prev,
      duration_hrs: nextDurationHrs,
      end_time: end,
      end_date: end,
      end_time_only: end,
    }));
  }, []);

  // Keep end_time in sync whenever duration_hrs changes (works for all activity types)
  useEffect(() => {
    if (!formData.duration_hrs) return;
    recalcEndFromDuration(formData.duration_hrs, formData.start_time);
  }, [formData.duration_hrs, formData.start_time, recalcEndFromDuration]);

  // Handle edit activity
  const handleEditActivity = useCallback((activity) => {
    //console.log('Editing activity:', activity);


    // Find the equipment from areaList
    const equipment = allAreas.find(eq => 
      eq.id === activity.equipment_id || 
      eq.id === activity.equipment_name || 
      eq.name === activity.equipment_name
    );
    
    // Parse start and end times, ensuring they're valid dayjs objects
    let startTime = null;
    let endTime = null;
    
    if (activity.start_time) {
      startTime = dayjs.isDayjs(activity.start_time) 
        ? activity.start_time 
        : dayjs(activity.start_time);
      if (!startTime.isValid()) {
        console.warn('Invalid start_time in activity:', activity.start_time);
        startTime = null;
      }
    }
    
    if (activity.end_time) {
      endTime = dayjs.isDayjs(activity.end_time)
        ? activity.end_time
        : dayjs(activity.end_time);
      if (!endTime.isValid()) {
        console.warn('Invalid end_time in activity:', activity.end_time);
        endTime = null;
      }
    }
    
    // Set selectedCell for display purposes
    if (equipment) {
      setSelectedCell({ 
        equipment: equipment, 
        day: { date: startTime || dayjs() }, 
        cellTime: startTime ? startTime.toDate() : new Date() 
      });
    }
    
    // Ensure date and time are properly separated for the pickers
    // DatePicker needs the full datetime (it extracts date part automatically)
    // TimePicker needs the full datetime (it extracts time part automatically)
    // Both should be dayjs objects
    const startDate = startTime ? (dayjs.isDayjs(startTime) ? startTime : dayjs(startTime)) : null;
    const startTimeOnly = startTime ? (dayjs.isDayjs(startTime) ? startTime : dayjs(startTime)) : null;
    const endDate = endTime ? (dayjs.isDayjs(endTime) ? endTime : dayjs(endTime)) : null;
    const endTimeOnly = endTime ? (dayjs.isDayjs(endTime) ? endTime : dayjs(endTime)) : null;

    // Derive duration (hrs) from start/end when editing (if both exist)
    let derivedDurationHrs = '';
    if (startTime && endTime) {
      const diffMinutes = endTime.diff(startTime, 'minute', true);
      if (Number.isFinite(diffMinutes) && diffMinutes > 0) {
        derivedDurationHrs = String(Math.round((diffMinutes / 60) * 100) / 100);
      }
    }
    
    // console.log('Edit Activity - Setting form data:', {
    //   startTime: startTime?.format('YYYY-MM-DD HH:mm:ss'),
    //   endTime: endTime?.format('YYYY-MM-DD HH:mm:ss'),
    //   startDate: startDate?.format('YYYY-MM-DD HH:mm:ss'),
    //   startTimeOnly: startTimeOnly?.format('YYYY-MM-DD HH:mm:ss'),
    //   endDate: endDate?.format('YYYY-MM-DD HH:mm:ss'),
    //   endTimeOnly: endTimeOnly?.format('YYYY-MM-DD HH:mm:ss'),
    //   product_name: activity.product_name,
    //   batch_number: activity.batch_number,
    //   fullActivity: activity
    // });
    
    setFormData({
      equipment_id: activity.equipment_id || equipment?.equipmentId || equipment?.id || null,
      equipment_name: activity.equipment_name || equipment?.name || '',
      activity_type: activity.activity_type || 'manufacturing',
      start_time: startTime,
      end_time: endTime,
      start_date: startDate, // Full datetime for DatePicker
      start_time_only: startTimeOnly, // Full datetime for TimePicker (it extracts time)
      end_date: endDate, // Full datetime for DatePicker
      end_time_only: endTimeOnly, // Full datetime for TimePicker (it extracts time)
      duration_hrs: derivedDurationHrs,
      batch_number: activity.batch_number || '',
      batch_id: activity.batch_number || null, // Keep for backward compatibility but not used
      product_name: activity.product_name || '', // Include product_name from activity if available
      volume_option: null, // Will be set after fetching options
      volume_option_payload: activity.option_selected || null, // Store original option_selected for API
      volume_option_data: null, // Will be set after fetching options
      cleaning_type: activity.cleaning_type || null,
      maintenance_type: activity.maintenance_type || null,
      remarks: activity.remarks || '' // Include remarks from activity
    });
    
    //console.log('=== Edit Activity - option_selected from activity ===', activity.option_selected);
    
    // Clear previous warning state
    setOptionMismatchWarning(false);
    setOriginalOptionSelected(null);
    
    // Fetch volume options if product_name exists (for editing)
    if (activity.product_name) {
      setLoadingVolumeOptions(true);
      fetchProductVolumeOptions(activity.product_name)
        .then(response => {
          const options = response.data?.options || [];
          setVolumeOptions(options);
          // Note: The useEffect watching processedVolumeOptions will handle matching
        })
        .catch(err => {
          console.error('Error loading volume options for edit:', err);
          setVolumeOptions([]);
        })
        .finally(() => setLoadingVolumeOptions(false));
    }
    
    setEditingActivityId(activity.id);
    setIsModalOpen(true);
  }, [allAreas]);

  // Toggle activity selection for multi-delete
  const toggleActivitySelection = useCallback((activityId) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  }, []);

  // Exit delete mode and clear selections
  const exitDeleteMode = useCallback(() => {
    setIsDeleteMode(false);
    setSelectedForDelete(new Set());
  }, []);

  // Exit reverse cascade delete mode
  const exitReverseCascadeDeleteMode = useCallback(() => {
    setIsReverseCascadeDeleteMode(false);
    setReverseCascadeEquipment(null);
    setSelectedForReverseCascadeDelete(new Set());
  }, []);

  // Toggle activity selection for reverse cascade delete
  const toggleReverseCascadeSelection = useCallback((activityId) => {
    setSelectedForReverseCascadeDelete(prev => {
      const next = new Set(prev);
      if (next.has(activityId)) {
        next.delete(activityId);
      } else {
        next.add(activityId);
      }
      return next;
    });
  }, []);

  // Handle multi-delete confirmation
  const handleMultiDelete = useCallback(() => {
    const selectedCount = selectedForDelete.size;
    if (selectedCount === 0) {
      message.warning('Please select at least one activity to delete');
      return;
    }

    // Get details of selected activities for display
    const selectedActivities = activities.filter(a => selectedForDelete.has(a.id));
    const activitySummary = selectedActivities.slice(0, 5).map(a => 
      `• ${a.activity_type}: ${a.batch_number || a.product_name || 'N/A'} (${dayjs(a.start_time).format('DD/MM HH:mm')})`
    ).join('\n');
    const moreText = selectedCount > 5 ? `\n... and ${selectedCount - 5} more` : '';

    Modal.confirm({
      title: `Delete ${selectedCount} Activities`,
      icon: <ExclamationCircleOutlined style={{ color: '#ef4444' }} />,
      content: (
        <div>
          <p style={{ color: '#ef4444', fontWeight: '500', marginBottom: '12px' }}>
            ⚠️ This action cannot be undone!
          </p>
          <p style={{ marginBottom: '8px' }}>You are about to delete the following activities:</p>
          <pre style={{ 
            fontSize: '12px', 
            backgroundColor: '#f3f4f6', 
            padding: '8px', 
            borderRadius: '4px',
            whiteSpace: 'pre-wrap',
            maxHeight: '150px',
            overflow: 'auto'
          }}>
            {activitySummary}{moreText}
          </pre>
        </div>
      ),
      okText: `Yes, Delete ${selectedCount} Activities`,
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingMultiple(true);
        let successCount = 0;
        let failCount = 0;

        try {
          // Delete each selected activity (no cascade). Concurrent rather than
          // one after another: runBatched attempts every row exactly once and
          // reports failures per row, so the counts below are unchanged.
          const result = await runBatched(
            Array.from(selectedForDelete),
            (activityId) => createEquipmentPlan({ id: activityId, is_active: false })
          );
          successCount = result.ok.length;
          failCount = result.failed.length;
          result.failed.forEach(({ item, error }) => {
            console.error(`Failed to delete activity ${item}:`, error);
          });

          // Show result message
          if (failCount === 0) {
            message.success(`Successfully deleted ${successCount} activities`);
          } else {
            message.warning(`Deleted ${successCount} activities, ${failCount} failed`);
          }

          // Refresh and exit delete mode
          await loadActivities();
          exitDeleteMode();
        } catch (error) {
          console.error('Error during multi-delete:', error);
          message.error('Failed to complete deletion');
        } finally {
          setDeletingMultiple(false);
        }
      }
    });
  }, [selectedForDelete, activities, loadActivities, exitDeleteMode]);

  // Reset form
  const handleReset = () => {
    if (selectedCell) {
      const startTime = dayjs(selectedCell.cellTime);
      setFormData({
        equipment_id: selectedCell.equipment.equipmentId || selectedCell.equipment.id, // Use the actual equipment ID
        equipment_name: selectedCell.equipment.name,
        activity_type: 'manufacturing',
        start_time: startTime,
        end_time: null, // Empty by default
      start_date: startTime,
      start_time_only: startTime,
      end_date: null,
      end_time_only: null,
      duration_hrs: '',
      batch_number: '',
        batch_id: null,
        product_name: '',
        volume_option: null,
        volume_option_payload: null,
        volume_option_data: null,
        number_of_batches: 0,
        cleaning_type_between: null,
        cleaning_duration_between: '',
        cleaning_type: null,
        maintenance_type: null,
        remarks: '',
        scheduling_mode: 'auto',
        manual_batches: [{
          batch_number: '',
          product_name: '',
          volume_option: null,
          volume_option_payload: null,
          volume_option_data: null,
          duration_hrs: '',
          cleaning_type: null,
          cleaning_duration_hrs: '',
          end_time: null
        }]
      });
    }
    // Clear volume options when resetting
    setVolumeOptions([]);
    setSelectedVolumeOption(null);
    setEditingActivityId(null);
    // Clear option mismatch warning
    setOptionMismatchWarning(false);
    setOriginalOptionSelected(null);
  };

  // Format week range for display
  const weekRangeText = useMemo(() => {
    return `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`;
  }, [weekStart, weekEnd]);

  // Format date to "YYYY-MM-DD HH:mm:ss"
  const formatDateTime = useCallback((date) => {
    if (!date) return null;
    const d = dayjs(date);
    return d.format('YYYY-MM-DD HH:mm:ss');
  }, []);

  // Helper function to check for overlaps with existing activities
  // Returns: { hasOverlap: boolean, overlappingActivities: array, message: string }
  // excludeActivityId accepts a single id or a list of them. A cut passes the
  // whole list of its source rows: they are about to be removed, so they must
  // not block a paste back into the same area.
  const checkForOverlaps = useCallback((equipmentId, newStart, newEnd, excludeActivityId = null) => {
    const newStartTime = dayjs(newStart);
    const newEndTime = dayjs(newEnd);

    const excludedIds = toExcludedSet(excludeActivityId);

    // Get all activities for this equipment
    const equipmentActivities = activities.filter(activity => {
      const activityEquipmentId = activity.equipment_id || activity.equipment_name || activity.eqp_id;
      if (activityEquipmentId !== equipmentId) return false;
      if (excludedIds.has(activity.id)) return false;
      return true;
    });

    const overlappingActivities = [];
    
    for (const activity of equipmentActivities) {
      const activityStart = dayjs(activity.start_time);
      const activityEnd = dayjs(activity.end_time);
      
      // Check for overlap: two time ranges overlap if:
      // newStart < activityEnd AND newEnd > activityStart
      const hasOverlap = newStartTime.valueOf() < activityEnd.valueOf() && 
                         newEndTime.valueOf() > activityStart.valueOf();
      
      if (hasOverlap) {
        overlappingActivities.push({
          ...activity,
          activityStart,
          activityEnd
        });
      }
    }

    if (overlappingActivities.length > 0) {
      const overlapDetails = overlappingActivities.map(a => 
        `${a.activity_type} (${dayjs(a.start_time).format('DD/MM HH:mm')} - ${dayjs(a.end_time).format('DD/MM HH:mm')})`
      ).join(', ');
      
      return {
        hasOverlap: true,
        overlappingActivities,
        message: `Overlap detected with: ${overlapDetails}`
      };
    }

    return {
      hasOverlap: false,
      overlappingActivities: [],
      message: ''
    };
  }, [activities]);

  // Helper to get consistent equipment ID from activity
  const getEquipmentId = useCallback((activity) => {
    return activity.equipment_id || activity.eqp_id || activity.equipment_name || null;
  }, []);

  /* ------------------------------------------------------------------ *
   * Cut / copy / paste
   *
   * Ctrl+X does not touch the database. The held blocks are only parked in
   * memory and the source bars stay live (dashed) until a paste succeeds, so
   * nothing can be lost by a refused paste or a change of mind. The write order
   * is create-then-delete, inside the hook.
   * ------------------------------------------------------------------ */
  const {
    clipboard,
    isHolding,
    heldIds,
    pasting,
    hold: holdInClipboard,
    clear: clearClipboard,
    planPaste,
    commitPaste,
  } = useAreaPlanClipboard({
    createPlan: createEquipmentPlan,
    checkForOverlaps,
  });

  const areaNameFor = useCallback((areaId) => (
    allAreas.find(area => area.equipmentId === areaId)?.name || areaId
  ), [allAreas]);

  const exitClipboardMode = useCallback(() => {
    setIsClipboardMode(false);
    setClipboardArea(null);
    setSelectedForClipboard(new Set());
  }, []);

  // Selection locks to the area of the first bar clicked.
  const toggleClipboardSelection = useCallback((activity) => {
    const areaId = getEquipmentId(activity);

    if (clipboardArea && areaId !== clipboardArea) {
      message.info(`Only activities in ${areaNameFor(clipboardArea)} can be selected`);
      return;
    }
    // Compute the next selection first, then set both pieces of state. Calling
    // a setter inside another setter's updater would run twice under StrictMode.
    const next = new Set(selectedForClipboard);
    if (next.has(activity.id)) {
      next.delete(activity.id);
    } else {
      next.add(activity.id);
    }

    setSelectedForClipboard(next);
    // Releasing the last selection unlocks the area again.
    setClipboardArea(next.size === 0 ? null : areaId);
  }, [clipboardArea, selectedForClipboard, getEquipmentId, areaNameFor]);

  const handleHold = useCallback((op) => {
    if (!isLatestVersion) {
      message.warning('Switch to the latest version to move activities');
      return;
    }
    if (selectedForClipboard.size === 0) {
      message.warning('Select at least one activity first');
      return;
    }

    const selected = activities.filter(activity => selectedForClipboard.has(activity.id));
    if (holdInClipboard(op, selected, clipboardArea, areaNameFor(clipboardArea))) {
      message.success(
        `${selected.length} ${selected.length === 1 ? 'activity' : 'activities'} ` +
        `${op === 'cut' ? 'cut' : 'copied'} - click an area to paste`
      );
      exitClipboardMode();
    }
  }, [
    isLatestVersion, selectedForClipboard, activities, clipboardArea,
    holdInClipboard, areaNameFor, exitClipboardMode
  ]);

  // Resolve a paste target and open the confirm. Nothing is written yet.
  const openPasteConfirm = useCallback((areaId, targetTime) => {
    const planned = planPaste(areaId, targetTime, weekEnd);
    if (!planned) return;
    setPasteTarget({
      planned,
      targetAreaId: areaId,
      targetAreaName: areaNameFor(areaId),
      targetTime: dayjs(targetTime),
    });
  }, [planPaste, weekEnd, areaNameFor]);

  // Re-resolve when the planner nudges the time in the confirm.
  const handlePasteTimeChange = useCallback((nextTime) => {
    if (!nextTime || !pasteTarget) return;
    openPasteConfirm(pasteTarget.targetAreaId, nextTime);
  }, [pasteTarget, openPasteConfirm]);

  const handlePasteConfirm = useCallback(async () => {
    if (!pasteTarget) return;
    const { planned, targetAreaName } = pasteTarget;
    const wasCut = planned.op === 'cut';
    // commitPaste clears the clipboard, so read what we need off it first.
    const sourceAreaName = clipboard?.sourceAreaName;

    try {
      const { created, deleteFailures } = await commitPaste(planned);
      setPasteTarget(null);
      await loadActivities();

      if (deleteFailures.length > 0) {
        // The create landed but a source row would not go away, so the activity
        // now sits in both areas. Say so plainly rather than leave a duplicate
        // for someone to find later.
        const stranded = deleteFailures
          .map(item => item.batch_number || item.activity_type)
          .join(', ');
        Modal.warning({
          title: 'Pasted, but the old copy is still there',
          content: (
            <div style={{ fontSize: '13px' }}>
              <p>
                {created} {created === 1 ? 'activity was' : 'activities were'} created in{' '}
                <strong>{targetAreaName}</strong>, but {deleteFailures.length}{' '}
                could not be removed from{' '}
                <strong>{sourceAreaName}</strong>:
              </p>
              <p style={{ fontWeight: 500 }}>{stranded}</p>
              <p>Please delete {deleteFailures.length === 1 ? 'it' : 'them'} manually.</p>
            </div>
          ),
        });
      } else {
        message.success(
          `${created} ${created === 1 ? 'activity' : 'activities'} ` +
          `${wasCut ? 'moved' : 'copied'} to ${targetAreaName}`
        );
      }
    } catch (error) {
      console.error('Paste failed:', error);
      // Nothing was deleted, and the clipboard is still held, so the planner can
      // simply try again.
      message.error(error.message || 'Could not paste - nothing was changed');
    }
  }, [pasteTarget, commitPaste, loadActivities, clipboard]);

  // Ctrl+X / Ctrl+C / Ctrl+V / Esc.
  //
  // Ctrl+V cannot act on its own -- the target area and day come from a cell
  // click -- so it prompts for that instead of guessing.
  useEffect(() => {
    const onKeyDown = (event) => {
      // Never steal a keystroke meant for a text field or an open form, or
      // Ctrl+C in the Remarks box would copy plan blocks instead of text.
      if (isModalOpen || pasteTarget) return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable) return;

      const key = event.key?.toLowerCase();

      if (key === 'escape') {
        if (isHolding) {
          clearClipboard();
          message.info('Clipboard cleared');
        } else if (isClipboardMode) {
          exitClipboardMode();
        }
        return;
      }

      if (!(event.ctrlKey || event.metaKey)) return;

      if (key === 'x' || key === 'c') {
        if (!isClipboardMode || selectedForClipboard.size === 0) return;
        event.preventDefault();
        handleHold(key === 'x' ? 'cut' : 'copy');
      } else if (key === 'v') {
        if (!isHolding) return;
        event.preventDefault();
        message.info('Click the area and day where you want to paste');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isModalOpen, pasteTarget, isHolding, isClipboardMode, selectedForClipboard,
    handleHold, clearClipboard, exitClipboardMode
  ]);

  // A held clipboard cannot be pasted into a published history version, so
  // switching away from the latest version drops it. Nothing was written, so
  // there is nothing to undo.
  useEffect(() => {
    if (!isLatestVersion && isHolding) {
      clearClipboard();
      message.info('Clipboard cleared - switch to the latest version to paste');
    }
  }, [isLatestVersion, isHolding, clearClipboard]);

  // Helper function to find activities that need to be shifted
  // Used for both pre-validation and actual cascade
  const findActivitiesToShift = useCallback((equipmentId, newEnd, excludeActivityId = null, newStart = null, freshActivities = null) => {
    const newEndTime = dayjs(newEnd);
    const newStartTime = newStart ? dayjs(newStart) : newEndTime; // If no start provided, use end as reference
    
    // Use fresh data if provided (avoids stale React closure), otherwise fallback to state
    const sourceActivities = freshActivities || activities;
    
    // Get all activities for this equipment that start at or after the new activity's start time
    const equipmentActivities = sourceActivities
      .filter(activity => {
        const activityEquipmentId = getEquipmentId(activity);
        if (activityEquipmentId !== equipmentId) return false;
        if (excludeActivityId && activity.id === excludeActivityId) return false;
        return true;
      })
      .map(activity => ({
        ...activity,
        startTime: dayjs(activity.start_time),
        endTime: dayjs(activity.end_time)
      }))
      .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

    const activitiesToShift = [];
    let currentReferenceEnd = newEndTime;

    for (const activity of equipmentActivities) {
      // Skip activities that START before the new activity's start time
      // These activities came first and should not be shifted
      if (activity.startTime.valueOf() < newStartTime.valueOf()) {
        continue;
      }
      
      // Check if this activity would overlap
      const wouldOverlap = activity.startTime.valueOf() < currentReferenceEnd.valueOf();
      
      if (wouldOverlap) {
        const duration = activity.endTime.diff(activity.startTime, 'minute');
        const newStart = currentReferenceEnd;
        const newEnd = newStart.add(duration, 'minute');
        
        activitiesToShift.push({
          ...activity,
          originalStart: activity.startTime,
          originalEnd: activity.endTime,
          newStart,
          newEnd
        });
        
        currentReferenceEnd = newEnd;
      } else {
        // No overlap, but update reference for subsequent checks
        currentReferenceEnd = activity.endTime;
      }
    }

    return activitiesToShift;
  }, [activities, getEquipmentId]);

  // Helper function to find activities that would shift BACKWARD after a delete
  // Used for pre-validation preview and actual reverse cascade
  // Algorithm: All subsequent activities on the same equipment shift back by the deleted activity's duration
  // Existing gaps between activities are preserved
  const findActivitiesToShiftBack = useCallback((equipmentId, deletedStart, deletedEnd, freshActivities = null) => {
    const delStart = dayjs(deletedStart);
    const delEnd = dayjs(deletedEnd);
    const shiftAmount = delEnd.diff(delStart, 'minute'); // Duration of deleted activity in minutes
    
    if (shiftAmount <= 0) return [];
    
    const sourceActivities = freshActivities || activities;
    
    // Get all activities for this equipment that start at or after the deleted activity's end
    const equipmentActivities = sourceActivities
      .filter(activity => {
        const activityEquipmentId = getEquipmentId(activity);
        if (activityEquipmentId !== equipmentId) return false;
        return true;
      })
      .map(activity => ({
        ...activity,
        startTime: dayjs(activity.start_time),
        endTime: dayjs(activity.end_time)
      }))
      .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

    const activitiesToShift = [];

    for (const activity of equipmentActivities) {
      // Only shift activities that start at or after the deleted activity's end time
      if (activity.startTime.valueOf() < delEnd.valueOf()) {
        continue;
      }

      // Shift back by the deleted activity's duration, preserving existing gaps
      const newStart = activity.startTime.subtract(shiftAmount, 'minute');
      const duration = activity.endTime.diff(activity.startTime, 'minute');
      const newEnd = newStart.add(duration, 'minute');

      activitiesToShift.push({
        ...activity,
        originalStart: activity.startTime,
        originalEnd: activity.endTime,
        newStart,
        newEnd
      });
    }

    return activitiesToShift;
  }, [activities, getEquipmentId]);

  // Reverse cascade after delete - shifts subsequent activities BACKWARD to fill the gap
  // Day supplier for the two cascade "Phase 2" future searches: fetches the
  // candidate days in concurrent chunks instead of one sequential GET per day.
  // The implementation and its sequential-equivalence tests live in
  // areaPlan/cascadeFetch.js.
  const createDayFetcher = useCallback(
    (startDate, maxDays, chunkSize = 14) =>
      makeDayFetcher(fetchEqpPlanner, startDate, maxDays, chunkSize),
    []
  );


  // Two-phase approach matching forward cascade:
  // Phase 1: Process in-memory activities (freshActivities from loaded 14-day window)
  // Phase 2: Search future dates via API for activities beyond loaded data
  const cascadeAfterDelete = useCallback(async (equipmentId, deletedStart, deletedEnd, freshActivities = null) => {
    const delStart = dayjs(deletedStart);
    const delEnd = dayjs(deletedEnd);
    const shiftAmount = delEnd.diff(delStart, 'minute');
    
    if (shiftAmount <= 0) return;

    console.log(`\n⬅️ REVERSE CASCADE STARTED`);
    console.log(`   Equipment: ${equipmentId}`);
    console.log(`   Deleted range: ${delStart.format('YYYY-MM-DD HH:mm')} → ${delEnd.format('YYYY-MM-DD HH:mm')}`);
    console.log(`   Shift amount: ${shiftAmount} minutes (${Math.round(shiftAmount / 60 * 10) / 10} hours)`);

    try {
      const sourceActivities = freshActivities || activities;
      const processedActivityIds = new Set();

      // PHASE 1: Process in-memory activities
      const equipmentActivities = sourceActivities
        .filter(activity => {
          const activityEquipmentId = getEquipmentId(activity);
          if (activityEquipmentId !== equipmentId) return false;
          return true;
        })
        .map(activity => ({
          ...activity,
          startTime: dayjs(activity.start_time),
          endTime: dayjs(activity.end_time)
        }))
        .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

      for (const activity of equipmentActivities) {
        if (activity.startTime.valueOf() < delEnd.valueOf()) {
          processedActivityIds.add(activity.id);
          continue;
        }

        processedActivityIds.add(activity.id);

        const newStart = activity.startTime.subtract(shiftAmount, 'minute');
        const duration = activity.endTime.diff(activity.startTime, 'minute');
        const newEnd = newStart.add(duration, 'minute');

        console.log(`  ⬅️ Shifting "${activity.activity_type}" [ID: ${activity.id}]`);
        console.log(`     Original: ${activity.startTime.format('YYYY-MM-DD HH:mm')} - ${activity.endTime.format('YYYY-MM-DD HH:mm')}`);
        console.log(`     Shifted:  ${newStart.format('YYYY-MM-DD HH:mm')} - ${newEnd.format('YYYY-MM-DD HH:mm')}`);

        const cascadePayload = {
          id: activity.id,
          eqp_plan_type: activity.activity_type === 'manufacturing' ? 'Operation' :
                         activity.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
          eqp_activity: activity.cleaning_type || activity.maintenance_type || '',
          eqp_id: equipmentId,
          start_date: formatDateTime(newStart),
          end_date: formatDateTime(newEnd),
          dispensing_request_id: activity.batch_number || '',
          product_name: activity.product_name || '',
          option_selected: activity.option_selected || ''
        };

        try {
          await createEquipmentPlan(cascadePayload);
          console.log(`     ✅ Updated successfully`);
        } catch (err) {
          console.error(`     ❌ Failed to update activity ${activity.id}:`, err);
        }
      }

      // PHASE 2: Search future dates for activities beyond loaded data
      const loadedEndDate = weekStart.add(13, 'day').endOf('day');
      const dayAfterLoaded = loadedEndDate.add(1, 'day').startOf('day');
      const startSearchDate = dayAfterLoaded;
      const maxSearchDays = 90;
      let consecutiveDaysEmpty = 0;
      const maxConsecutiveEmpty = 14;

      const nextDay = createDayFetcher(startSearchDate, maxSearchDays);

      for (let dayOffset = 0; dayOffset < maxSearchDays && consecutiveDaysEmpty < maxConsecutiveEmpty; dayOffset++) {
        const dateToCheck = startSearchDate.add(dayOffset, 'day');
        const dateStr = dateToCheck.format('YYYY-MM-DD');

        try {
          const response = await nextDay(dayOffset);
          const activitiesData = response?.activities || response?.data || [];

          const relevantActivities = activitiesData
            .filter(item => {
              const itemEquipmentId = item.eqp_id || item.equipment_id || item.equipment_name;
              if (itemEquipmentId !== equipmentId) return false;
              if (processedActivityIds.has(item.id)) return false;
              const itemStart = dayjs(item.start_date || item.start_time);
              if (itemStart.valueOf() < delEnd.valueOf()) return false;
              return true;
            })
            .map(item => ({
              ...item,
              parsedStart: dayjs(item.start_date || item.start_time),
              parsedEnd: dayjs(item.end_date || item.end_time)
            }))
            .sort((a, b) => a.parsedStart.valueOf() - b.parsedStart.valueOf());

          if (relevantActivities.length === 0) {
            consecutiveDaysEmpty++;
            continue;
          }

          consecutiveDaysEmpty = 0;

          for (const item of relevantActivities) {
            const newStart = item.parsedStart.subtract(shiftAmount, 'minute');
            const duration = item.parsedEnd.diff(item.parsedStart, 'minute');
            const newEnd = newStart.add(duration, 'minute');

            console.log(`  ⬅️ Future shift: ${item.eqp_plan_type}`);
            console.log(`     Original: ${item.parsedStart.format('YYYY-MM-DD HH:mm')} - ${item.parsedEnd.format('YYYY-MM-DD HH:mm')}`);
            console.log(`     Shifted:  ${newStart.format('YYYY-MM-DD HH:mm')} - ${newEnd.format('YYYY-MM-DD HH:mm')}`);

            const cascadePayload = {
              id: item.id,
              eqp_plan_type: item.eqp_plan_type,
              eqp_activity: item.eqp_activity || '',
              eqp_id: equipmentId,
              start_date: formatDateTime(newStart),
              end_date: formatDateTime(newEnd),
              dispensing_request_id: item.dispensing_request_id || item.batch_number || '',
              product_name: item.product_name || '',
              option_selected: item.option_selected || ''
            };

            try {
              await createEquipmentPlan(cascadePayload);
              console.log(`     ✅ Updated successfully`);
              processedActivityIds.add(item.id);
            } catch (err) {
              console.error(`     ❌ Failed:`, err);
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Error checking date ${dateStr}:`, err);
          }
        }
      }

      console.log(`\n✅ REVERSE CASCADE COMPLETE - Processed ${processedActivityIds.size} activities total\n`);
    } catch (err) {
      console.error('❌ Error in reverse cascade:', err);
    }
  }, [activities, formatDateTime, weekStart, getEquipmentId, createDayFetcher]);

  // ============================================================================
  // TRAIN-MOVE CASCADE (for start-time rescheduling)
  // ============================================================================
  // When a user changes the START time of an activity, every non-past activity
  // after it on the same equipment moves with it by the same delta, preserving
  // relative gaps. A collision with any non-train activity blocks the save.
  //
  // Helpers below are pure (no side effects except API calls in commit/revert).

  // Build the train: all non-past activities with start >= editedOldStart on the
  // same equipment, excluding the edited activity itself.
  // Returns { trainMembers, skippedPast } where each is an array of activity objects
  // (with parsed startTime/endTime fields).
  const buildTrain = useCallback((editedActivity, editedOldStart, freshActivities) => {
    const equipmentId = getEquipmentId(editedActivity);
    const oldStart = dayjs(editedOldStart);
    const now = dayjs();
    const source = freshActivities || activities;

    const candidates = source
      .filter(a => {
        if (a.id === editedActivity.id) return false;
        if (getEquipmentId(a) !== equipmentId) return false;
        const st = dayjs(a.start_time);
        if (st.valueOf() < oldStart.valueOf()) return false;
        return true;
      })
      .map(a => ({
        ...a,
        startTime: dayjs(a.start_time),
        endTime: dayjs(a.end_time)
      }))
      .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

    const trainMembers = [];
    const skippedPast = [];
    for (const a of candidates) {
      if (a.startTime.isBefore(now)) {
        skippedPast.push(a);
      } else {
        trainMembers.push(a);
      }
    }
    return { trainMembers, skippedPast };
  }, [activities, getEquipmentId]);

  // Calculate shifts for train members given a delta in minutes.
  // Returns an array of { activity, oldStart, oldEnd, newStart, newEnd, duration }.
  const calculateTrainShift = useCallback((trainMembers, deltaMinutes) => {
    return trainMembers.map(a => {
      const duration = a.endTime.diff(a.startTime, 'minute');
      const newStart = a.startTime.add(deltaMinutes, 'minute');
      const newEnd = newStart.add(duration, 'minute');
      return {
        activity: a,
        oldStart: a.startTime,
        oldEnd: a.endTime,
        newStart,
        newEnd,
        duration
      };
    });
  }, []);

  // Check if the shifted train (edited activity + train members at their new
  // positions) overlaps any non-train activity on the same equipment.
  // Non-train = activities that started before editedOldStart, plus any skipped
  // past activities. Returns array of conflicting activities (with details).
  const checkTrainCollisions = useCallback((editedActivity, editedNewStart, editedNewEnd, shifts, skippedPast, editedOldStart, freshActivities) => {
    const equipmentId = getEquipmentId(editedActivity);
    const oldStart = dayjs(editedOldStart);
    const source = freshActivities || activities;

    // Non-train activities: same equipment, excluding edited, excluding train members.
    const trainIds = new Set(shifts.map(s => s.activity.id));
    const nonTrain = source
      .filter(a => {
        if (a.id === editedActivity.id) return false;
        if (getEquipmentId(a) !== equipmentId) return false;
        if (trainIds.has(a.id)) return false;
        return true;
      })
      .map(a => ({
        ...a,
        startTime: dayjs(a.start_time),
        endTime: dayjs(a.end_time)
      }));

    // Include skipped past activities explicitly (they weren't in `source` filter
    // above because their start >= oldStart, but they ARE in the skippedPast bucket).
    for (const p of skippedPast) {
      if (!nonTrain.find(n => n.id === p.id)) nonTrain.push(p);
    }

    const editedStartMs = dayjs(editedNewStart).valueOf();
    const editedEndMs = dayjs(editedNewEnd).valueOf();

    const collisions = [];

    // Check edited activity's new range against every non-train activity
    for (const nt of nonTrain) {
      const ntStart = nt.startTime.valueOf();
      const ntEnd = nt.endTime.valueOf();
      if (ntStart < editedEndMs && ntEnd > editedStartMs) {
        collisions.push({ activity: nt, conflictWith: 'edited' });
      }
    }

    // Check each shifted train member against every non-train activity
    for (const s of shifts) {
      const sStart = s.newStart.valueOf();
      const sEnd = s.newEnd.valueOf();
      for (const nt of nonTrain) {
        const ntStart = nt.startTime.valueOf();
        const ntEnd = nt.endTime.valueOf();
        if (ntStart < sEnd && ntEnd > sStart) {
          if (!collisions.find(c => c.activity.id === nt.id)) {
            collisions.push({ activity: nt, conflictWith: s.activity });
          }
        }
      }
    }

    return collisions;
  }, [activities, getEquipmentId]);

  // Commit the train shift: PATCH each train member to its new start/end.
  // Returns a snapshot array for undo: [{ id, oldStart, oldEnd, activity }].
  const commitTrainShift = useCallback(async (shifts, equipmentId) => {
    const snapshot = [];
    for (const s of shifts) {
      const a = s.activity;
      snapshot.push({
        id: a.id,
        oldStart: s.oldStart,
        oldEnd: s.oldEnd,
        activity: a
      });

      const payload = {
        id: a.id,
        eqp_plan_type: a.activity_type === 'manufacturing' ? 'Operation' :
                       a.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
        eqp_activity: a.cleaning_type || a.maintenance_type || '',
        eqp_id: equipmentId,
        start_date: formatDateTime(s.newStart),
        end_date: formatDateTime(s.newEnd),
        dispensing_request_id: a.batch_number || '',
        product_name: a.product_name || '',
        option_selected: a.option_selected || ''
      };

      try {
        await createEquipmentPlan(payload);
        console.log(`  [CASCADE] shifted [ID ${a.id}] ${a.activity_type} ${s.oldStart.format('DD/MM HH:mm')} → ${s.newStart.format('DD/MM HH:mm')}`);
      } catch (err) {
        console.error(`  ❌ Failed to shift [ID ${a.id}]:`, err);
        throw err;
      }
    }
    return snapshot;
  }, [formatDateTime]);

  // Revert train shift using snapshot.
  const revertTrainShift = useCallback(async (snapshot, equipmentId) => {
    for (const entry of snapshot) {
      const a = entry.activity;
      const payload = {
        id: entry.id,
        eqp_plan_type: a.activity_type === 'manufacturing' ? 'Operation' :
                       a.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
        eqp_activity: a.cleaning_type || a.maintenance_type || '',
        eqp_id: equipmentId,
        start_date: formatDateTime(entry.oldStart),
        end_date: formatDateTime(entry.oldEnd),
        dispensing_request_id: a.batch_number || '',
        product_name: a.product_name || '',
        option_selected: a.option_selected || ''
      };
      try {
        await createEquipmentPlan(payload);
      } catch (err) {
        console.error(`  ❌ Undo failed for [ID ${entry.id}]:`, err);
      }
    }
  }, [formatDateTime]);

  // Handle delete activity (with optional reverse cascade)
  const handleDeleteActivity = useCallback(async (activity) => {
    const equipmentId = getEquipmentId(activity);
    const deletedStart = activity.start_time;
    const deletedEnd = activity.end_time;

    Modal.confirm({
      title: 'Delete Activity',
      content: `Are you sure you want to delete this ${activity.activity_type} activity?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // Soft-delete the activity
          const payload = {
            id: activity.id,
            is_active: false
          };
          await createEquipmentPlan(payload);
          message.success('Activity deleted successfully!');

          // Fetch fresh data after deletion
          const freshData = await loadActivities();

          // Check if subsequent activities can be shifted back
          const activitiesToShiftBack = findActivitiesToShiftBack(equipmentId, deletedStart, deletedEnd, freshData);

          if (activitiesToShiftBack.length > 0) {
            const shiftDetails = activitiesToShiftBack.slice(0, 10).map(a =>
              `• ${a.activity_type}: ${a.originalStart.format('DD/MM HH:mm')} → ${a.newStart.format('DD/MM HH:mm')}`
            ).join('\n');
            const moreText = activitiesToShiftBack.length > 10 ? `\n... and ${activitiesToShiftBack.length - 10} more` : '';

            const confirmed = await new Promise((resolve) => {
              Modal.confirm({
                title: `${activitiesToShiftBack.length} activity(ies) can be shifted back`,
                icon: <ExclamationCircleOutlined />,
                content: (
                  <div>
                    <p>The following activities can be shifted backward to fill the gap:</p>
                    <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                      {shiftDetails}{moreText}
                    </pre>
                    <p>Do you want to shift them?</p>
                  </div>
                ),
                okText: 'Yes, Shift Back',
                cancelText: 'No, Leave Gap',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });

            if (confirmed) {
              try {
                const latestData = await loadActivities();
                await cascadeAfterDelete(equipmentId, deletedStart, deletedEnd, latestData);
                await loadActivities();
                message.success('Activities shifted back successfully!');
              } catch (cascadeErr) {
                console.error('⚠️  Reverse cascade error:', cascadeErr);
                message.warning('Activity deleted, but some dependent activities may need manual adjustment');
              }
            }
          }
        } catch (error) {
          console.error('Error deleting activity:', error);
          message.error(error.message || 'Failed to delete activity. Please try again.');
        }
      }
    });
  }, [loadActivities, getEquipmentId, findActivitiesToShiftBack, cascadeAfterDelete]);

  // Handle reverse cascade delete (bulk) - delete selected + shift subsequent back
  const handleReverseCascadeDelete = useCallback(async () => {
    const selectedCount = selectedForReverseCascadeDelete.size;
    if (selectedCount === 0) {
      message.warning('Please select at least one activity to delete');
      return;
    }
    if (!reverseCascadeEquipment) {
      message.warning('Please select an area first');
      return;
    }

    // Get details of selected activities, sorted by start time
    const selectedActivities = activities
      .filter(a => selectedForReverseCascadeDelete.has(a.id))
      .map(a => ({ ...a, startTime: dayjs(a.start_time), endTime: dayjs(a.end_time) }))
      .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

    if (selectedActivities.length === 0) {
      message.warning('Could not find selected activities');
      return;
    }

    // Calculate the combined gap: earliest start to latest end of all selected
    const combinedStart = selectedActivities[0].startTime;
    const combinedEnd = selectedActivities[selectedActivities.length - 1].endTime;
    
    // Calculate total duration of selected activities (sum of individual durations)
    const totalDeletedDuration = selectedActivities.reduce((sum, a) => {
      return sum + a.endTime.diff(a.startTime, 'minute');
    }, 0);

    // Build summary for confirmation
    const activitySummary = selectedActivities.slice(0, 5).map(a =>
      `• ${a.activity_type}: ${a.batch_number || a.product_name || 'N/A'} (${a.startTime.format('DD/MM HH:mm')} - ${a.endTime.format('HH:mm')})`
    ).join('\n');
    const moreText = selectedCount > 5 ? `\n... and ${selectedCount - 5} more` : '';

    // Preview which activities will shift back
    const activitiesToShiftBack = findActivitiesToShiftBack(
      reverseCascadeEquipment, combinedStart, combinedEnd
    );
    // Filter out the selected-for-delete activities from shift preview
    const actualShifts = activitiesToShiftBack.filter(a => !selectedForReverseCascadeDelete.has(a.id));

    const shiftPreview = actualShifts.length > 0
      ? actualShifts.slice(0, 8).map(a =>
          `• ${a.activity_type}: ${a.originalStart.format('DD/MM HH:mm')} → ${a.newStart.format('DD/MM HH:mm')}`
        ).join('\n') + (actualShifts.length > 8 ? `\n... and ${actualShifts.length - 8} more` : '')
      : 'No subsequent activities to shift.';

    Modal.confirm({
      title: `Delete ${selectedCount} Activities & Shift Back`,
      icon: <ExclamationCircleOutlined style={{ color: '#d97706' }} />,
      content: (
        <div>
          <p style={{ color: '#ef4444', fontWeight: '500', marginBottom: '12px' }}>
            ⚠️ This action cannot be undone!
          </p>
          <p style={{ marginBottom: '4px', fontWeight: '500' }}>Activities to delete:</p>
          <pre style={{
            fontSize: '12px', backgroundColor: '#fef2f2', padding: '8px',
            borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto'
          }}>
            {activitySummary}{moreText}
          </pre>
          {actualShifts.length > 0 && (
            <>
              <p style={{ marginTop: '12px', marginBottom: '4px', fontWeight: '500' }}>Activities that will shift back:</p>
              <pre style={{
                fontSize: '12px', backgroundColor: '#f0fdf4', padding: '8px',
                borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto'
              }}>
                {shiftPreview}
              </pre>
            </>
          )}
        </div>
      ),
      okText: `Delete & Shift Back`,
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingWithCascade(true);
        try {
          // Step 1: Soft-delete all selected activities, concurrently.
          const deleteResult = await runBatched(
            Array.from(selectedForReverseCascadeDelete),
            (activityId) => createEquipmentPlan({ id: activityId, is_active: false })
          );
          const successCount = deleteResult.ok.length;
          const failCount = deleteResult.failed.length;
          deleteResult.failed.forEach(({ item, error }) => {
            console.error(`Failed to delete activity ${item}:`, error);
          });

          if (failCount > 0) {
            message.warning(`Deleted ${successCount} activities, ${failCount} failed`);
          }

          // Step 2: Reverse cascade - shift subsequent activities back
          if (actualShifts.length > 0) {
            try {
              const freshData = await loadActivities();
              await cascadeAfterDelete(reverseCascadeEquipment, combinedStart, combinedEnd, freshData);
            } catch (cascadeErr) {
              console.error('⚠️  Reverse cascade error:', cascadeErr);
              message.warning('Activities deleted, but some shifts may need manual adjustment');
            }
          }

          // Step 3: Refresh and exit mode
          await loadActivities();
          exitReverseCascadeDeleteMode();
          message.success(`${successCount} activities deleted and subsequent activities shifted back!`);
        } catch (error) {
          console.error('Error during reverse cascade delete:', error);
          message.error('Failed to complete operation');
        } finally {
          setDeletingWithCascade(false);
        }
      }
    });
  }, [selectedForReverseCascadeDelete, reverseCascadeEquipment, activities, loadActivities, findActivitiesToShiftBack, cascadeAfterDelete, exitReverseCascadeDeleteMode]);

  // Handle cascading updates for dependent activities
  // COMPREHENSIVE OVERLAP PREVENTION AND CASCADE LOGIC
  // Rules:
  // 1. No two activities can overlap on the same equipment (manufacturing, cleaning, or maintenance)
  // 2. If an activity's time changes, ALL subsequent activities must shift if they would overlap
  // 3. Existing gaps should be preserved unless consumed by extensions
  // 4. Works for both CREATE and UPDATE operations
  // 
  // Key Algorithm:
  // - Process activities in chronological order by start time
  // - Track "currentReferenceEnd" which represents the earliest time the next activity can start
  // - For each activity:
  //   a) If it ends before the edited/created activity starts → skip (not affected)
  //   b) If it starts >= currentReferenceEnd → no overlap, but UPDATE reference to this activity's end
  //   c) If it starts < currentReferenceEnd → overlap! Shift it and update reference
  // 
  // @param skipActivityIds - Set of activity IDs to skip (e.g., newly created batch activities)
  const cascadeActivityUpdates = useCallback(async (editingActivityId, oldEndTime, newEndTime, newActivityStart = null, newActivityEnd = null, equipmentIdParam = null, skipActivityIds = new Set(), freshActivities = null) => {
    if (!editingActivityId) return;

    const newEnd = dayjs(newEndTime);
    const newStart = newActivityStart ? dayjs(newActivityStart) : null;
    const isCreateOperation = newActivityStart !== null && newActivityEnd !== null;

    console.log(`\n🔄 CASCADE STARTED (${isCreateOperation ? 'CREATE' : 'EDIT'})`);
    console.log(`   Activity being ${isCreateOperation ? 'created' : 'edited'}: ${editingActivityId}`);
    if (isCreateOperation) {
      console.log(`   New activity range: ${newStart?.format('YYYY-MM-DD HH:mm:ss')} → ${newEnd.format('YYYY-MM-DD HH:mm:ss')}`);
    } else {
      console.log(`   Old end: ${dayjs(oldEndTime).format('YYYY-MM-DD HH:mm:ss')} → New end: ${newEnd.format('YYYY-MM-DD HH:mm:ss')}`);
    }
    if (skipActivityIds.size > 0) {
      console.log(`   Skipping ${skipActivityIds.size} newly created activity IDs`);
    }

    try {
      // Determine the equipment ID
      let equipmentId = equipmentIdParam;
      let editingActivity = null;
      
      // Use fresh data if provided (avoids stale React closure), otherwise fallback to state
      const sourceActivities = freshActivities || activities;
      
      if (!isCreateOperation) {
        // For EDIT operations, find the edited activity
        editingActivity = sourceActivities.find(a => a.id === editingActivityId);
        if (!editingActivity) {
          console.warn('❌ Could not find editing activity for cascade update');
          return;
        }
        equipmentId = editingActivity.equipment_id || editingActivity.equipment_name;
      }

      if (!equipmentId) {
        console.warn('❌ No equipment ID available for cascade');
        return;
      }

      console.log(`🔍 Processing cascade for equipment: ${equipmentId}`);

      // Get the edited/created activity's time range for overlap detection
      const editedActivityStart = isCreateOperation ? dayjs(newActivityStart) : dayjs(editingActivity.start_time);
      const editedActivityNewEnd = isCreateOperation ? dayjs(newActivityEnd) : newEnd;

      // Get ALL activities for this equipment, sorted by start time
      // This ensures we process them in chronological order
      // Skip activities in the skipActivityIds set (newly created activities)
      const equipmentActivities = sourceActivities
        .filter(activity => {
          const activityEquipmentId = getEquipmentId(activity);
          // Skip if not same equipment
          if (activityEquipmentId !== equipmentId) return false;
          // Skip the activity being edited
          if (activity.id === editingActivityId) return false;
          // Skip newly created activities (for batch mode)
          if (skipActivityIds.has(activity.id)) return false;
          return true;
        })
        .map(activity => ({
          ...activity,
          startTime: dayjs(activity.start_time),
          endTime: dayjs(activity.end_time)
        }))
        .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());

      console.log(`Found ${equipmentActivities.length} other activities for this equipment`);
      console.log(`📋 Activities to process:`);
      equipmentActivities.forEach((a, idx) => {
        console.log(`   ${idx + 1}. [ID: ${a.id}] ${a.activity_type}: ${a.startTime.format('DD/MM HH:mm')} - ${a.endTime.format('HH:mm')}`);
      });

      // Track ALL processed activities (both shifted and non-shifted)
      // This prevents double-processing when searching future dates
      const processedActivityIds = new Set();
      if (editingActivityId) processedActivityIds.add(editingActivityId);

      // The reference end time - starts with the edited/created activity's new end time
      // This represents the earliest point where the next activity can safely start
      // CRITICAL: This must be updated after processing EACH activity (shifted or not)
      let currentReferenceEnd = editedActivityNewEnd;
      console.log(`🎯 Starting reference end: ${currentReferenceEnd.format('DD/MM HH:mm')}`);

      // Process each activity in chronological order
      for (const activity of equipmentActivities) {
        const activityStart = activity.startTime;
        const activityEnd = activity.endTime;
        const activityDuration = activityEnd.diff(activityStart, 'minute');

        // Skip activities that START before the new/edited activity's START time
        // These activities came first and should not be shifted - only subsequent activities cascade
        if (activityStart.valueOf() < editedActivityStart.valueOf()) {
          console.log(`  ⏭️  Activity "${activity.activity_type}" [ID: ${activity.id}] (${activityStart.format('DD/MM HH:mm')} - ${activityEnd.format('DD/MM HH:mm')}) - Starts before new activity, skipping`);
          processedActivityIds.add(activity.id); // Mark as processed to avoid re-processing in future search
          continue;
        }

        // Mark this activity as processed
        processedActivityIds.add(activity.id);

        // Check if this activity OVERLAPS with the current reference end time
        // Overlap means: activity starts BEFORE reference end
        const wouldOverlap = activityStart.valueOf() < currentReferenceEnd.valueOf();

        if (!wouldOverlap) {
          // Activity starts at or after current reference ends - no conflict
          // There's a gap, so we DON'T need to shift this activity
          console.log(`  ✓ Activity "${activity.activity_type}" [ID: ${activity.id}] (${activityStart.format('DD/MM HH:mm')} - ${activityEnd.format('DD/MM HH:mm')}) - No conflict, gap preserved. Ref end updated from ${currentReferenceEnd.format('DD/MM HH:mm')} → ${activityEnd.format('DD/MM HH:mm')}`);
          
          // CRITICAL FIX: Update reference to this activity's end time
          // Even though we didn't shift this activity, subsequent activities must 
          // respect this activity's time slot. This ensures proper cascading when:
          // - Activity A is edited (extended)
          // - Activity B has a gap (no shift needed)
          // - Activity C (after B) would now overlap with B
          // Without this update, C would only check against A's end, missing the B overlap
          currentReferenceEnd = activityEnd;
          continue;
        }

        // Activity would overlap - need to shift it
        const newActivityStart = currentReferenceEnd;
        const newActivityEnd = newActivityStart.add(activityDuration, 'minute');

        console.log(`  ⚠️  Activity "${activity.activity_type}" [ID: ${activity.id}] overlaps!`);
        console.log(`     Current ref end: ${currentReferenceEnd.format('DD/MM HH:mm')}`);
        console.log(`     Original: ${activityStart.format('YYYY-MM-DD HH:mm')} - ${activityEnd.format('YYYY-MM-DD HH:mm')}`);
        console.log(`     Shifted:  ${newActivityStart.format('YYYY-MM-DD HH:mm')} - ${newActivityEnd.format('YYYY-MM-DD HH:mm')}`);
        console.log(`     Duration: ${activityDuration} minutes`);

        // Build payload for the API call
        const cascadePayload = {
          id: activity.id,
          eqp_plan_type: activity.activity_type === 'manufacturing' ? 'Operation' : 
                         activity.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
          eqp_activity: activity.cleaning_type || activity.maintenance_type || '',
          eqp_id: equipmentId,
          start_date: formatDateTime(newActivityStart),
          end_date: formatDateTime(newActivityEnd),
          dispensing_request_id: activity.batch_number || '',
          product_name: activity.product_name || '',
          option_selected: activity.option_selected || ''
        };

        try {
          await createEquipmentPlan(cascadePayload);
          console.log(`     ✅ Updated successfully`);

          // Update the reference end time for the next iteration
          // The next activity should start after this one ends
          currentReferenceEnd = newActivityEnd;
        } catch (err) {
          console.error(`     ❌ Failed to update activity ${activity.id}:`, err);
          // Even if update fails, we should still update reference to avoid cascading errors
          currentReferenceEnd = newActivityEnd;
        }
      }

      // SECOND: Search for dependent activities in future dates (beyond loaded data)
      // ONLY search if we actually shifted any activities AND the last shifted activity
      // extends beyond what we have in memory
      // SKIP this for now - the in-memory cascade should handle most cases
      // The issue is that the API returns UPDATED data, causing double-processing
      
      // Check if we need to search future dates
      // Only do this if the cascade pushed activities beyond the loaded date range
      const loadedEndDate = weekStart.add(13, 'day').endOf('day'); // We load 14 days
      const needsFutureSearch = currentReferenceEnd.isAfter(loadedEndDate);
      
      // ALWAYS search future dates for cascade - activities can be planned up to 3 months ahead
      if (needsFutureSearch || true) {
        console.log(`\n📅 Checking for activities in future dates (up to 3 months ahead)...`);
        console.log(`   Current reference end: ${currentReferenceEnd.format('YYYY-MM-DD HH:mm')}`);
        console.log(`   Loaded data ends: ${loadedEndDate.format('YYYY-MM-DD HH:mm')}`);
        
        // CRITICAL: Start searching from the day after loaded data ends OR currentReferenceEnd,
        // whichever is EARLIER. This prevents a blind spot when currentReferenceEnd jumps far ahead
        // (e.g., user creates activities from 3 weeks back that span into future weeks) but there
        // are existing activities between the loaded data boundary and currentReferenceEnd
        // that Phase 1 never saw because they were outside the 14-day loaded window.
        // Example: weekStart=Apr28, loaded=Apr28-May11, existing activities May14-24,
        //          new batch ends May20 → without this fix Phase 2 starts at May20,
        //          completely missing May12-19 activities → overlaps.
        const dayAfterLoaded = loadedEndDate.add(1, 'day').startOf('day');
        const refStartDay = currentReferenceEnd.startOf('day');
        const startSearchDate = dayAfterLoaded.isBefore(refStartDay) ? dayAfterLoaded : refStartDay;
        const maxSearchDays = 90; // Look ahead 3 months
        let consecutiveDaysEmpty = 0;
        const maxConsecutiveEmpty = 14; // Stop early after 2 weeks of no activities for this equipment
        let foundLargeGap = false;

        const nextDay = createDayFetcher(startSearchDate, maxSearchDays);

        for (let dayOffset = 0; dayOffset < maxSearchDays && consecutiveDaysEmpty < maxConsecutiveEmpty && !foundLargeGap; dayOffset++) {
          const dateToCheck = startSearchDate.add(dayOffset, 'day');
          const dateStr = dateToCheck.format('YYYY-MM-DD');
          
          try {
            const response = await nextDay(dayOffset);
            const activitiesData = response?.activities || response?.data || [];
            
            // Sort activities by start time
            const relevantActivities = activitiesData
              .filter(item => {
                const itemEquipmentId = item.eqp_id || item.equipment_id || item.equipment_name;
                // Skip if not same equipment
                if (itemEquipmentId !== equipmentId) return false;
                // Skip if already processed (shifted or not) in main loop
                if (processedActivityIds.has(item.id)) return false;
                // Skip newly created activities
                if (skipActivityIds.has(item.id)) return false;
                // Skip activities that START before the edited activity's start time
                const itemStart = dayjs(item.start_date || item.start_time);
                if (itemStart.valueOf() < editedActivityStart.valueOf()) return false;
                return true;
              })
              .map(item => ({
                ...item,
                parsedStart: dayjs(item.start_date || item.start_time),
                parsedEnd: dayjs(item.end_date || item.end_time)
              }))
              .sort((a, b) => a.parsedStart.valueOf() - b.parsedStart.valueOf());

            if (relevantActivities.length === 0) {
              consecutiveDaysEmpty++;
              continue;
            }

            consecutiveDaysEmpty = 0;

            for (const item of relevantActivities) {
              const startTime = item.parsedStart;
              const endTime = item.parsedEnd;
              const duration = endTime.diff(startTime, 'minute');

              // Check if this activity overlaps with our reference
              if (startTime.valueOf() >= currentReferenceEnd.valueOf()) {
                // No overlap - log gap info but continue searching
                const gapHours = startTime.diff(currentReferenceEnd, 'hour');
                console.log(`   ✓ [Phase2] Activity "${item.eqp_plan_type}" [ID: ${item.id}] (${startTime.format('YYYY-MM-DD HH:mm')} - ${endTime.format('YYYY-MM-DD HH:mm')}) - No conflict, gap: ${gapHours}h. Ref end → ${endTime.format('DD/MM HH:mm')}`);
                
                // Mark as processed so we don't revisit, even though no shift needed
                processedActivityIds.add(item.id);
                
                // Update reference for next iteration
                currentReferenceEnd = endTime;
                continue;
              }

              // Overlap detected - shift this activity
              const newStart = currentReferenceEnd;
              const newEnd = newStart.add(duration, 'minute');

              console.log(`   ⚠️  [Phase2] Future activity overlaps: ${item.eqp_plan_type} [ID: ${item.id}]`);
              console.log(`      Original: ${startTime.format('YYYY-MM-DD HH:mm')} - ${endTime.format('YYYY-MM-DD HH:mm')}`);
              console.log(`      Shifted:  ${newStart.format('YYYY-MM-DD HH:mm')} - ${newEnd.format('YYYY-MM-DD HH:mm')}`);

              const cascadePayload = {
                id: item.id,
                eqp_plan_type: item.eqp_plan_type,
                eqp_activity: item.eqp_activity || '',
                eqp_id: equipmentId,
                start_date: formatDateTime(newStart),
                end_date: formatDateTime(newEnd),
                dispensing_request_id: item.dispensing_request_id || item.batch_number || '',
                product_name: item.product_name || '',
                option_selected: item.option_selected || ''
              };

              try {
                await createEquipmentPlan(cascadePayload);
                console.log(`      ✅ Updated successfully`);
                processedActivityIds.add(item.id);
                currentReferenceEnd = newEnd;
              } catch (err) {
                console.error(`      ❌ Failed:`, err);
                currentReferenceEnd = newEnd;
              }
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`Error checking date ${dateStr}:`, err);
            }
          }
        }
      } else {
        console.log(`\n📅 Skipping future dates search - all activities within loaded range`);
      }

      console.log(`\n✅ CASCADE COMPLETE - Processed ${processedActivityIds.size - 1} activities total\n`);
    } catch (err) {
      console.error('❌ Error cascading activity updates:', err);
    }
  }, [activities, formatDateTime, weekStart, getEquipmentId, createDayFetcher]);

  // Submit form
  const handleSubmit = useCallback(async () => {
    //console.log('=== VALIDATION START ===');
    //console.log('Current Form Data:', formData);
    
    if (!formData.equipment_id) {
      //console.log('❌ Validation Failed: No equipment selected');
      message.error('Please select an area');
      return;
    }
    // Check for start time - either combined or separate fields
    let startTime = formData.start_time;
    if (!startTime && formData.start_date) {
        if (formData.start_time_only) {
        startTime = formData.start_date
          .hour(formData.start_time_only.hour())
          .minute(formData.start_time_only.minute())
          .second(0)
          .millisecond(0);
      } else {
        startTime = formData.start_date.startOf('day');
      }
    }
    
    if (!startTime) {
      //console.log('❌ Validation Failed: No start time');
      message.error('Please select a start time');
      return;
    }
    
    // Check for end time - either combined or separate fields
    let endTime = formData.end_time;

    // If duration is provided, auto-calculate endTime from startTime
    if ((!endTime || !dayjs(endTime).isValid()) && formData.duration_hrs) {
      const duration = Number(formData.duration_hrs);
      if (!Number.isFinite(duration) || duration <= 0) {
        //console.log('❌ Validation Failed: Invalid duration');
        message.error('Please enter a valid duration (hrs)');
        return;
      }
      endTime = dayjs(startTime).add(duration, 'hour');
    }

    if (!endTime && formData.end_date) {
      if (formData.end_time_only) {
        endTime = formData.end_date
          .hour(formData.end_time_only.hour())
          .minute(formData.end_time_only.minute())
          .second(0)
          .millisecond(0);
      } else {
        endTime = formData.end_date.endOf('day');
      }
    }
    
    // For manual scheduling mode, validate differently
    if (formData.activity_type === 'manufacturing' && formData.scheduling_mode === 'manual') {
      // Validate product name
      if (!formData.product_name) {
        //console.log('❌ Validation Failed: No product name for manufacturing');
        message.error('Please enter a product name');
        return;
      }
      // Validate manual batches
      const validBatches = formData.manual_batches.filter(b => b.batch_number && b.duration_hrs);
      if (validBatches.length === 0) {
        //console.log('❌ Validation Failed: No valid batches in manual mode');
        message.error('Please enter at least one batch with batch number and duration');
        return;
      }
    } else {
      // Auto scheduling mode validation
      if (!endTime) {
        //console.log('❌ Validation Failed: No end time');
        message.error('Please select an end time');
        return;
      }
      if (formData.activity_type === 'manufacturing' && !formData.batch_number) {
        //console.log('❌ Validation Failed: No batch number for manufacturing');
        message.error('Please enter a batch number');
        return;
      }
      if (formData.activity_type === 'manufacturing' && !formData.product_name) {
        //console.log('❌ Validation Failed: No product name for manufacturing');
        message.error('Please enter a product name');
        return;
      }
    }
    if (formData.activity_type === 'cleaning' && !formData.cleaning_type) {
      //console.log('❌ Validation Failed: No cleaning type');
      message.error('Please select a cleaning type');
      return;
    }
    if (formData.activity_type === 'maintenance' && !formData.maintenance_type) {
      //console.log('❌ Validation Failed: No maintenance type');
      message.error('Please select a maintenance type');
      return;
    }
    
    //console.log('✅ Validation Passed');
    //console.log('=== VALIDATION END ===\n');

    setSubmitting(true);
    try {
      // Ensure we have the combined start_time and end_time
      let startTime = formData.start_time;
      if (!startTime && formData.start_date) {
        if (formData.start_time_only) {
          startTime = formData.start_date
            .hour(formData.start_time_only.hour())
            .minute(formData.start_time_only.minute())
            .second(0)
            .millisecond(0);
        } else {
          startTime = formData.start_date.startOf('day');
        }
      }
      
      let endTime = formData.end_time;

      // If duration is provided, auto-calculate endTime from startTime
      if ((!endTime || !dayjs(endTime).isValid()) && formData.duration_hrs) {
        const duration = Number(formData.duration_hrs);
        if (!Number.isFinite(duration) || duration <= 0) {
          message.error('Please enter a valid duration (hrs)');
          return;
        }
        endTime = dayjs(startTime).add(duration, 'hour');
      }

      if (!endTime && formData.end_date) {
        if (formData.end_time_only) {
          endTime = formData.end_date
            .hour(formData.end_time_only.hour())
            .minute(formData.end_time_only.minute())
            .second(0)
            .millisecond(0);
        } else {
          endTime = formData.end_date.endOf('day');
        }
      }

      // ======= PRE-SUBMISSION OVERLAP CHECK =======
      // For CREATE operations (not edit), check if the new activity's time slot has overlaps
      // If overlaps exist, the cascade function will handle shifting them
      // We log this information for debugging purposes
      if (!editingActivityId) {
        console.log('\n🔍 PRE-SUBMISSION OVERLAP CHECK');
        console.log(`   Equipment: ${formData.equipment_id}`);
        console.log(`   New activity range: ${dayjs(startTime).format('YYYY-MM-DD HH:mm')} - ${dayjs(endTime).format('YYYY-MM-DD HH:mm')}`);
        const overlapCheck = checkForOverlaps(formData.equipment_id, startTime, endTime);
        if (overlapCheck.hasOverlap) {
          console.log(`⚠️  Overlap detected: ${overlapCheck.message}`);
          console.log('   These activities will be automatically shifted after creation.');

          // Show the activities that will be shifted (pass startTime as 4th param)
          const activitiesToShift = findActivitiesToShift(formData.equipment_id, endTime, null, startTime);
          if (activitiesToShift.length > 0) {
            console.log(`📋 Activities that will be shifted (${activitiesToShift.length}):`);
            activitiesToShift.forEach(a => {
              console.log(`   - ${a.activity_type} [ID: ${a.id}]: ${a.originalStart.format('DD/MM HH:mm')} → ${a.newStart.format('DD/MM HH:mm')}`);
            });
          }
        } else {
          console.log('✅ No overlaps detected with existing activities');
        }
        console.log('');
      }
      // ======= END OVERLAP CHECK =======
      
      // Build payload based on activity type
      let payloads = []; // Array to hold all payloads
      let payload; // Single payload for non-batch operations
      
      // Handle batch generation for manufacturing
      let batchesToCreate = [];
      
      if (formData.activity_type === 'manufacturing') {
        // Check scheduling mode
        if (formData.scheduling_mode === 'manual') {
          // Manual scheduling mode - process each manual batch entry
          const validBatches = formData.manual_batches.filter(b => b.batch_number && b.duration_hrs);
          let currentBatchStart = dayjs(startTime);
          
          for (let i = 0; i < validBatches.length; i++) {
            const batch = validBatches[i];
            const durationHrs = Number(batch.duration_hrs);
            const batchEndTime = currentBatchStart.add(durationHrs, 'hour');
            // Use batch-specific product_name, fallback to main product_name
            const batchProductName = batch.product_name || formData.product_name || '';
            
            // Add batch payload
            payloads.push({
              eqp_plan_type: 'Operation',
              eqp_activity: 'PRODUCTION',
              eqp_id: formData.equipment_id,
              start_date: formatDateTime(currentBatchStart),
              end_date: formatDateTime(batchEndTime),
              dispensing_request_id: batch.batch_number,
              product_name: batchProductName,
              option_selected: batch.volume_option_payload || formData.volume_option_payload || '',
              remarks: formData.remarks || ''
            });
            
            // Add cleaning after this batch if specified (except for last batch)
            if (batch.cleaning_type && batch.cleaning_duration_hrs && i < validBatches.length - 1) {
              const cleaningDurationHrs = Number(batch.cleaning_duration_hrs);
              const cleaningStartTime = batchEndTime;
              const cleaningEndTime = cleaningStartTime.add(cleaningDurationHrs, 'hour');
              
              payloads.push({
                eqp_plan_type: 'Cleaning',
                eqp_activity: batch.cleaning_type,
                eqp_id: formData.equipment_id,
                start_date: formatDateTime(cleaningStartTime),
                end_date: formatDateTime(cleaningEndTime),
                dispensing_request_id: '',
                product_name: batchProductName,
                option_selected: '',
                remarks: formData.remarks || ''
              });
              
              // Next batch starts after cleaning
              currentBatchStart = cleaningEndTime;
            } else {
              // Next batch starts after this batch
              currentBatchStart = batchEndTime;
            }
          }
        } else if (formData.number_of_batches > 0) {
          // Auto scheduling mode - generate batch numbers based on starting batch and count
          const match = formData.batch_number.match(/^([A-Za-z]+)?(\d+)$/);
          if (match) {
            const prefix = match[1] || '';
            const startNum = parseInt(match[2]);
            const numLength = match[2].length;
            const durationHrs = Number(formData.duration_hrs);
            const cleaningDurationHrs = Number(formData.cleaning_duration_between) || 0;
            const hasCleaningBetween = formData.cleaning_type_between && cleaningDurationHrs > 0;
            
            // Start from the user-entered start time
            let currentBatchStart = dayjs(startTime);
            
            for (let i = 0; i < formData.number_of_batches; i++) {
              const batchNum = String(startNum + i).padStart(numLength, '0');
              const batchName = `${prefix}${batchNum}`;
              
              // Each batch starts where the previous one ended
              const batchStartTime = currentBatchStart;
              const batchEndTime = batchStartTime.add(durationHrs, 'hour');
              
              // Add batch payload
              payloads.push({
                eqp_plan_type: 'Operation',
                eqp_activity: 'PRODUCTION',
                eqp_id: formData.equipment_id,
                start_date: formatDateTime(batchStartTime),
                end_date: formatDateTime(batchEndTime),
                dispensing_request_id: batchName,
                product_name: formData.product_name || '',
                option_selected: formData.volume_option_payload || '',
                remarks: formData.remarks || ''
              });
              
              // Add cleaning between batches (except after the last batch)
              if (hasCleaningBetween && i < formData.number_of_batches - 1) {
                const cleaningStartTime = batchEndTime;
                const cleaningEndTime = cleaningStartTime.add(cleaningDurationHrs, 'hour');
                
                payloads.push({
                  eqp_plan_type: 'Cleaning',
                  eqp_activity: formData.cleaning_type_between || '',
                  eqp_id: formData.equipment_id,
                  start_date: formatDateTime(cleaningStartTime),
                  end_date: formatDateTime(cleaningEndTime),
                  dispensing_request_id: '',
                  product_name: formData.product_name || '',
                  option_selected: '',
                  remarks: formData.remarks || ''
                });
                
                // Update start time for next batch (cleaning end becomes next batch start)
                currentBatchStart = cleaningEndTime;
              } else {
                // Update start time for next batch (current batch end becomes next start)
                currentBatchStart = batchEndTime;
              }
            }
          }
        } else {
          // Single batch mode (original behavior)
          payload = {
            eqp_plan_type: 'Operation',
            eqp_activity: 'PRODUCTION',
            eqp_id: formData.equipment_id,
            start_date: formatDateTime(startTime),
            end_date: formatDateTime(endTime),
            dispensing_request_id: formData.batch_number || '',
            product_name: formData.product_name || '',
            option_selected: formData.volume_option_payload || '',
            remarks: formData.remarks || ''
          };
        }
      } else if (formData.activity_type === 'cleaning') {
        // Cleaning payload
        payload = {
          eqp_plan_type: 'Cleaning',
          eqp_activity: formData.cleaning_type || '',
          eqp_id: formData.equipment_id,
          start_date: formatDateTime(startTime),
          end_date: formatDateTime(endTime),
          dispensing_request_id: '',
          product_name: formData.product_name || '',
          option_selected: '',
          remarks: formData.remarks || ''
        };
      } else {
        // Maintenance payload
        payload = {
          eqp_plan_type: 'maintainance',
          eqp_activity: formData.maintenance_type || '',
          eqp_id: formData.equipment_id,
          start_date: formatDateTime(startTime),
          end_date: formatDateTime(endTime),
          dispensing_request_id: '',
          product_name: formData.product_name || '',
          option_selected: '',
          remarks: formData.remarks || ''
        };
      }

      //console.log('=== CREATE ACTIVITY SUBMISSION ===');
      //console.log('Form Data:', formData);
      //console.log('Equipment ID being sent:', formData.equipment_id);
      //console.log('Equipment Name:', formData.equipment_name);
      
      // Log payloads being sent
      if (payloads.length > 0) {
        //console.log('Payloads to create:', payloads.length);
        //console.log('=== PAYLOAD DETAILS ===');
        payloads.forEach((p, idx) => {
          // console.log(`Payload ${idx + 1}:`, {
          //   type: p.eqp_plan_type,
          //   activity: p.eqp_activity,
          //   dispensing_request_id: p.dispensing_request_id,
          //   start_date: p.start_date,
          //   end_date: p.end_date
          // });
        });
        //console.log('=== END PAYLOAD DETAILS ===');
      } else if (payload) {
        //console.log('Payload:', payload);
        //console.log('Payload JSON:', JSON.stringify(payload, null, 2));
      }
      
      let response;
      let oldEndTime = null;
      let newActivityId = null;
      
      if (editingActivityId) {
        // Can't edit in batch mode - only single activity edits
        const originalActivity = activities.find(a => a.id === editingActivityId);
        oldEndTime = originalActivity?.end_time;
        
        // Update existing activity
        payload.id = editingActivityId;
        response = await createEquipmentPlan(payload);
      } else {
        // Create new activity/activities.
        //
        // Refuse rather than write an overlap. Until now the overlap check
        // above only wrote to the console and the create went ahead regardless,
        // which is how a second batch train could land exactly on top of an
        // existing one. Every generated row is checked, not just the form's
        // overall range, because auto and manual batch modes expand one form
        // into N rows spanning days.
        const rowsToCheck = payloads.length > 0 ? payloads : (payload ? [payload] : []);
        const blockers = new Map();
        let blockedRows = 0;

        rowsToCheck.forEach(row => {
          if (!row?.start_date || !row?.end_date) return;
          const result = checkForOverlaps(row.eqp_id, row.start_date, row.end_date);
          if (result.hasOverlap) {
            blockedRows += 1;
            result.overlappingActivities.forEach(a => blockers.set(a.id, a));
          }
        });

        if (blockers.size > 0) {
          const areaLabel = areaNameFor(formData.equipment_id);
          const lines = Array.from(blockers.values())
            .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf())
            .slice(0, 10)
            .map(a => `• ${a.activity_type}${a.batch_number ? ` ${a.batch_number}` : ''}${a.cleaning_type ? ` ${a.cleaning_type}` : ''}: `
              + `${dayjs(a.start_time).format('DD/MM HH:mm')} - ${a.end_time ? dayjs(a.end_time).format('DD/MM HH:mm') : 'running'}`)
            .join('\n');
          const more = blockers.size > 10 ? `\n... and ${blockers.size - 10} more` : '';

          Modal.warning({
            title: 'Cannot create: those hours are already planned',
            icon: <ExclamationCircleOutlined style={{ color: '#dc2626' }} />,
            width: 560,
            content: (
              <div>
                <p>
                  {blockedRows === rowsToCheck.length
                    ? `This activity overlaps work already planned in ${areaLabel}:`
                    : `${blockedRows} of the ${rowsToCheck.length} activities you are creating overlap work already planned in ${areaLabel}:`}
                </p>
                <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto', background: '#fef2f2', padding: '8px', borderRadius: '4px' }}>
                  {lines}{more}
                </pre>
                <p>Move or delete them first, or pick a time that is free. Nothing has been created.</p>
              </div>
            ),
            okText: 'OK'
          });
          return; // the finally below resets the submitting state
        }

        if (payloads.length > 0) {
          // Send array of payloads in a single request
          try {
            //console.log('📤 Sending array of payloads...');
            //console.log(`📊 Total payloads: ${payloads.length}`);
            response = await createEquipmentPlan(payloads);
            //console.log('✅ All payloads created successfully');
          } catch (err) {
            console.error('❌ Error creating payloads:', err);
            throw err;
          }
        } else {
          // Create single activity
          response = await createEquipmentPlan(payload);
          newActivityId = response?.id || response?.data?.id;
        }
      }
      
      if (response.success) {
        //console.log('✅ Success - Activity', editingActivityId ? 'updated' : 'created');
      //console.log('===================================\n');
      
      // Store whether this was an edit or create before we clear the state
      const isEditOperation = !!editingActivityId;
      
      // Don't close modal yet - wait for cascade and reload to complete
        
        // EDIT CASE: branch on whether START time changed
        // - Start changed → TRAIN-MOVE (all subsequent non-past activities slide by same delta)
        // - Start unchanged, end changed → existing forward-cascade (gaps absorb, only overlaps shift)
        // - Neither changed → no cascade (e.g. batch name / product edit)
        if (editingActivityId && oldEndTime) {
          const originalActivity = activities.find(a => a.id === editingActivityId);
          const oldStart = originalActivity ? dayjs(originalActivity.start_time) : null;
          const newStart = dayjs(startTime);
          const oldEnd = dayjs(oldEndTime);
          const newEnd = dayjs(endTime);

          const startTimeChanged = oldStart && !oldStart.isSame(newStart);
          const endTimeChanged = !oldEnd.isSame(newEnd);

          if (startTimeChanged) {
            // ========== TRAIN-MOVE PATH ==========
            console.log('\n=== CASCADE UPDATE (EDIT — start time changed) ===');
            console.log(`   Edited: ${editingActivityId} | Old start: ${oldStart.format('YYYY-MM-DD HH:mm')} → New start: ${newStart.format('YYYY-MM-DD HH:mm')}`);

            const deltaMinutes = newStart.diff(oldStart, 'minute');
            console.log(`   Delta: ${deltaMinutes} min (${(deltaMinutes / 60).toFixed(1)}h)`);

            const freshData = await loadActivities();
            const editedFresh = freshData.find(a => a.id === editingActivityId) || originalActivity;

            const { trainMembers, skippedPast } = buildTrain(editedFresh, oldStart, freshData);
            console.log(`   Cascading activities: ${trainMembers.length} | Skipped (past): ${skippedPast.length}`);

            const shifts = calculateTrainShift(trainMembers, deltaMinutes);

            const collisions = checkTrainCollisions(
              editedFresh, newStart, newEnd, shifts, skippedPast, oldStart, freshData
            );

            if (collisions.length > 0) {
              console.log(`   Collisions detected with activities outside cascade scope: ${collisions.length}`);
              const conflictLines = collisions.slice(0, 8).map(c => {
                const who = c.conflictWith === 'edited' ? 'edited activity' : `shifted ${c.conflictWith.activity_type}`;
                return `• ${c.activity.activity_type} ${c.activity.batch_number || c.activity.product_name || ''} (${dayjs(c.activity.start_time).format('DD/MM HH:mm')} - ${dayjs(c.activity.end_time).format('HH:mm')}) overlaps ${who}`;
              }).join('\n');
              const more = collisions.length > 8 ? `\n... and ${collisions.length - 8} more` : '';

              await new Promise((resolve) => {
                Modal.warning({
                  title: 'Cannot move: would overlap other activities',
                  icon: <ExclamationCircleOutlined style={{ color: '#dc2626' }} />,
                  content: (
                    <div>
                      <p>Moving this activity would cause overlaps with activities that are not part of the cascade:</p>
                      <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto', background: '#fef2f2', padding: '8px', borderRadius: '4px' }}>
                        {conflictLines}{more}
                      </pre>
                      <p>Please choose a time that does not overlap existing activities.</p>
                    </div>
                  ),
                  okText: 'OK',
                  onOk: () => resolve(),
                });
              });

              // REVERT the edited activity to its original times
              try {
                const revertPayload = {
                  id: editingActivityId,
                  eqp_plan_type: originalActivity.activity_type === 'manufacturing' ? 'Operation' :
                                 originalActivity.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
                  eqp_activity: originalActivity.cleaning_type || originalActivity.maintenance_type || '',
                  eqp_id: formData.equipment_id,
                  start_date: formatDateTime(oldStart),
                  end_date: formatDateTime(oldEnd),
                  dispensing_request_id: originalActivity.batch_number || '',
                  product_name: originalActivity.product_name || '',
                  option_selected: originalActivity.option_selected || ''
                };
                await createEquipmentPlan(revertPayload);
              } catch (revertErr) {
                console.error('Failed to revert edit after collision:', revertErr);
              }
              await loadActivities();
              handleReset();
              setIsModalOpen(false);
              setEditingActivityId(null);
              return;
            }

            // Show preview dialog
            const direction = deltaMinutes >= 0 ? 'later' : 'earlier';
            const absHours = Math.abs(deltaMinutes) / 60;
            const shiftLines = shifts.slice(0, 15).map(s =>
              `• ${s.activity.activity_type} ${s.activity.batch_number || s.activity.product_name || ''}: ${s.oldStart.format('DD/MM HH:mm')} → ${s.newStart.format('DD/MM HH:mm')}`
            ).join('\n');
            const shiftMore = shifts.length > 15 ? `\n... and ${shifts.length - 15} more` : '';
            const skippedLines = skippedPast.slice(0, 5).map(p =>
              `• ${p.activity_type} ${p.batch_number || p.product_name || ''} (${p.startTime.format('DD/MM HH:mm')}) — already started/past`
            ).join('\n');

            const confirmed = await new Promise((resolve) => {
              Modal.confirm({
                title: `Cascade update: ${shifts.length + 1} activity(ies) will shift ${absHours.toFixed(1)}h ${direction}`,
                icon: <ExclamationCircleOutlined style={{ color: '#2563eb' }} />,
                width: 600,
                content: (
                  <div>
                    <p style={{ marginBottom: 8, fontWeight: 500 }}>Edited activity:</p>
                    <pre style={{ fontSize: '12px', background: '#eff6ff', padding: '8px', borderRadius: '4px', marginBottom: 12 }}>
                      {`• ${originalActivity.activity_type} ${originalActivity.batch_number || originalActivity.product_name || ''}: ${oldStart.format('DD/MM HH:mm')} → ${newStart.format('DD/MM HH:mm')}`}
                    </pre>
                    {shifts.length > 0 && (
                      <>
                        <p style={{ marginBottom: 8, fontWeight: 500 }}>Cascading activities ({shifts.length}):</p>
                        <pre style={{ fontSize: '12px', maxHeight: '220px', overflow: 'auto', background: '#f0fdf4', padding: '8px', borderRadius: '4px', marginBottom: 12 }}>
                          {shiftLines}{shiftMore}
                        </pre>
                      </>
                    )}
                    {skippedPast.length > 0 && (
                      <>
                        <p style={{ marginBottom: 8, fontWeight: 500, color: '#78716c' }}>Skipped (past activities, will not move):</p>
                        <pre style={{ fontSize: '12px', maxHeight: '100px', overflow: 'auto', background: '#f5f5f4', padding: '8px', borderRadius: '4px' }}>
                          {skippedLines}
                        </pre>
                      </>
                    )}
                  </div>
                ),
                okText: 'Confirm Cascade',
                cancelText: 'Cancel',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });

            if (!confirmed) {
              // User cancelled — revert the edited activity
              try {
                const revertPayload = {
                  id: editingActivityId,
                  eqp_plan_type: originalActivity.activity_type === 'manufacturing' ? 'Operation' :
                                 originalActivity.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
                  eqp_activity: originalActivity.cleaning_type || originalActivity.maintenance_type || '',
                  eqp_id: formData.equipment_id,
                  start_date: formatDateTime(oldStart),
                  end_date: formatDateTime(oldEnd),
                  dispensing_request_id: originalActivity.batch_number || '',
                  product_name: originalActivity.product_name || '',
                  option_selected: originalActivity.option_selected || ''
                };
                await createEquipmentPlan(revertPayload);
                message.info('Cancelled. Activity restored to original time.');
              } catch (revertErr) {
                console.error('Failed to revert edit:', revertErr);
                message.error('Cancelled, but failed to restore original time. Please check.');
              }
              await loadActivities();
              handleReset();
              setIsModalOpen(false);
              setEditingActivityId(null);
              return;
            }

            // Commit the cascade
            let snapshot = [];
            try {
              snapshot = await commitTrainShift(shifts, formData.equipment_id);
              console.log(`   Cascade committed: ${snapshot.length} activities updated`);
            } catch (commitErr) {
              console.error('Cascade error:', commitErr);
              message.error('Some activities failed to shift. Check plan for consistency.');
            }

            // Offer undo toast (30s)
            if (snapshot.length > 0) {
              const undoKey = `cascade-undo-${Date.now()}`;
              const equipmentIdForUndo = formData.equipment_id;
              const editedRevert = {
                id: editingActivityId,
                eqp_plan_type: originalActivity.activity_type === 'manufacturing' ? 'Operation' :
                               originalActivity.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
                eqp_activity: originalActivity.cleaning_type || originalActivity.maintenance_type || '',
                eqp_id: equipmentIdForUndo,
                start_date: formatDateTime(oldStart),
                end_date: formatDateTime(oldEnd),
                dispensing_request_id: originalActivity.batch_number || '',
                product_name: originalActivity.product_name || '',
                option_selected: originalActivity.option_selected || ''
              };
              message.success({
                content: (
                  <span>
                    Cascade complete ({snapshot.length + 1} activities updated).{' '}
                    <a
                      onClick={async () => {
                        message.destroy(undoKey);
                        message.loading({ content: 'Reverting cascade…', key: undoKey, duration: 0 });
                        try {
                          await revertTrainShift(snapshot, equipmentIdForUndo);
                          await createEquipmentPlan(editedRevert);
                          await loadActivities();
                          message.success({ content: 'Cascade reverted.', key: undoKey, duration: 3 });
                        } catch (undoErr) {
                          console.error('Undo failed:', undoErr);
                          message.error({ content: 'Undo failed. Check plan manually.', key: undoKey, duration: 5 });
                        }
                      }}
                      style={{ color: '#2563eb', fontWeight: 600, cursor: 'pointer', marginLeft: 8 }}
                    >
                      Undo
                    </a>
                  </span>
                ),
                key: undoKey,
                duration: 30,
              });
            }
          } else if (endTimeChanged) {
            // ========== END-ONLY CHANGE: existing forward-cascade path ==========
            console.log('\n🔄🔄🔄 END-ONLY EDIT — forward cascade (gaps absorb, only overlaps shift) 🔄🔄🔄');

            const activitiesToShift = findActivitiesToShift(formData.equipment_id, endTime, editingActivityId, startTime);

            if (activitiesToShift.length > 0) {
              const shiftDetails = activitiesToShift.map(a =>
                `• ${a.activity_type}: ${a.originalStart.format('DD/MM HH:mm')} → ${a.newStart.format('DD/MM HH:mm')}`
              ).join('\n');

              const confirmed = await new Promise((resolve) => {
                Modal.confirm({
                  title: `${activitiesToShift.length} activity(ies) will be shifted`,
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      <p>The following activities will be automatically shifted:</p>
                      <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                        {shiftDetails}
                      </pre>
                    </div>
                  ),
                  okText: 'Yes, Shift Activities',
                  cancelText: 'Cancel',
                  onOk: () => resolve(true),
                  onCancel: () => resolve(false),
                });
              });

              if (!confirmed) {
                message.info('Edit cancelled. Reverting changes...');
                try {
                  const revertPayload = {
                    id: editingActivityId,
                    eqp_plan_type: originalActivity.activity_type === 'manufacturing' ? 'Operation' :
                                   originalActivity.activity_type === 'cleaning' ? 'Cleaning' : 'maintainance',
                    eqp_activity: originalActivity.cleaning_type || originalActivity.maintenance_type || '',
                    eqp_id: formData.equipment_id,
                    start_date: formatDateTime(oldStart),
                    end_date: formatDateTime(oldEnd),
                    dispensing_request_id: originalActivity.batch_number || '',
                    product_name: originalActivity.product_name || '',
                    option_selected: originalActivity.option_selected || ''
                  };
                  await createEquipmentPlan(revertPayload);
                  message.success('Edit reverted successfully');
                } catch (revertErr) {
                  console.error('Failed to revert edit:', revertErr);
                  message.error('Failed to revert edit. Please manually fix the overlap.');
                }
                await loadActivities();
                handleReset();
                setIsModalOpen(false);
                setEditingActivityId(null);
                return;
              }
            }

            try {
              const freshData = await loadActivities();
              await cascadeActivityUpdates(editingActivityId, oldEndTime, endTime, null, null, null, new Set(), freshData);
            } catch (cascadeErr) {
              console.error('⚠️  Cascade error (continuing anyway):', cascadeErr);
              message.warning('Activity updated, but some dependent activities may need manual adjustment');
            }
          }
        }
        
        // CREATE CASE: If new activity was created, check for overlaps with EXISTING activities
        // The cascade will process all activities that would overlap with the new range
        if (!editingActivityId && startTime && endTime) {
          // Get IDs of newly created activities from response to skip them
          const newlyCreatedIds = new Set();
          if (response?.data && Array.isArray(response.data)) {
            response.data.forEach(item => {
              if (item.id) newlyCreatedIds.add(item.id);
            });
          } else if (response?.data?.id) {
            newlyCreatedIds.add(response.data.id);
          } else if (response?.id) {
            newlyCreatedIds.add(response.id);
          }
          
          // Calculate the full range of new activities
          // For single activity: startTime to endTime
          // For batch mode: first payload start to last payload end
          let finalStartTime = dayjs(startTime);
          let finalEndTime = dayjs(endTime);
          if (payloads.length > 0) {
            const firstPayload = payloads[0];
            const lastPayload = payloads[payloads.length - 1];
            finalStartTime = dayjs(firstPayload.start_date);
            finalEndTime = dayjs(lastPayload.end_date);
            console.log(`   Batch mode: Range = ${finalStartTime.format('YYYY-MM-DD HH:mm')} to ${finalEndTime.format('YYYY-MM-DD HH:mm')}`);
          }
          
          console.log('\n🔄🔄🔄 STARTING CASCADE UPDATE FOR DEPENDENT ACTIVITIES (CREATE) 🔄🔄🔄\n');
          console.log(`   New activity range: ${finalStartTime.format('YYYY-MM-DD HH:mm')} to ${finalEndTime.format('YYYY-MM-DD HH:mm')}`);
          console.log(`   Skipping ${newlyCreatedIds.size} newly created activity IDs`);
          
          // PRE-CHECK: Find activities that will be shifted BEFORE asking user
          const activitiesToShift = findActivitiesToShift(formData.equipment_id, finalEndTime, null, finalStartTime);
          
          if (activitiesToShift.length > 0) {
            // Show confirmation dialog
            const shiftDetails = activitiesToShift.map(a => 
              `• ${a.activity_type}: ${a.originalStart.format('DD/MM HH:mm')} → ${a.newStart.format('DD/MM HH:mm')}`
            ).join('\n');
            
            const confirmed = await new Promise((resolve) => {
              Modal.confirm({
                title: `${activitiesToShift.length} activity(ies) will be shifted`,
                icon: <ExclamationCircleOutlined />,
                content: (
                  <div>
                    <p>The following activities will be automatically shifted to prevent overlaps:</p>
                    <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                      {shiftDetails}
                    </pre>
                    <p>Do you want to continue?</p>
                  </div>
                ),
                okText: 'Yes, Shift Activities',
                cancelText: 'Cancel',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });
            
            if (!confirmed) {
              // User cancelled - delete the just-created activity
              message.info('Operation cancelled. Reverting...');
              const revert = await runBatched(
                Array.from(newlyCreatedIds),
                (id) => createEquipmentPlan({ id, is_active: false })
              );
              revert.failed.forEach(({ item, error }) => {
                console.error(`Error reverting activity ${item}:`, error);
              });
              if (revert.failed.length > 0) {
                // The rows that would not go away are still on the plan; say so
                // rather than leave the planner thinking the create was undone.
                message.warning(
                  `${revert.failed.length} of ${revert.ok.length + revert.failed.length} created ` +
                  `activities could not be removed. Please delete them manually.`
                );
              }
              await loadActivities();
              handleReset();
              setIsModalOpen(false);
              setEditingActivityId(null);
              return;
            }
          }
          
          try {
            // Fetch fresh data and pass directly to cascade (avoids stale React closure)
            const freshData = await loadActivities();
            
            await cascadeActivityUpdates(
              newActivityId || 'new_activity', 
              finalEndTime, 
              finalEndTime, 
              finalStartTime, 
              finalEndTime, 
              formData.equipment_id,
              newlyCreatedIds,  // Pass IDs to skip
              freshData  // Pass fresh data so Phase 1 sees current DB state
            );
          } catch (cascadeErr) {
            console.error('⚠️  Cascade error on create (continuing anyway):', cascadeErr);
          }
        }
        
        // Reload activities to show all updated ones
        console.log('\n📂 Reloading all activities...\n');
        await loadActivities();
        
        // Reset form
        handleReset();
        
        // NOW close modal after everything is complete
        //console.log('Closing modal...');
        setIsModalOpen(false);
        setEditingActivityId(null);
        
        // Show success message
        const successMsg = isEditOperation 
          ? 'Activity and dependents updated!'
          : (payloads.length > 0 
              ? `${payloads.length} items created successfully!`
              : 'Activity created successfully!');
        message.success(successMsg);
      } else {
        throw new Error(response.message || `Failed to ${editingActivityId ? 'update' : 'create'} area plan`);
      }
    } catch (err) {
      console.error('❌ Error', editingActivityId ? 'updating' : 'creating', 'plan:', err);
      const errorMessage = err.message || `Failed to ${editingActivityId ? 'update' : 'create'} area plan`;
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  // checkForOverlaps and areaNameFor are listed because the create path now
  // refuses overlaps with them - a stale closure here would mean checking
  // against an out-of-date activity list.
  }, [formData, loadActivities, editingActivityId, cascadeActivityUpdates, activities, buildTrain, calculateTrainShift, checkTrainCollisions, commitTrainShift, revertTrainShift, formatDateTime, findActivitiesToShift, checkForOverlaps, areaNameFor]);

  const getActivityColor = (type) => {
    const activity = activityTypes.find(a => a.value === type);
    return activity ? activity.color : '#6b7280';
  };

  const formatTime = (date) => {
    // Safari-safe date formatting
    if (!date) return '';
    try {
      // Use dayjs for consistent formatting across browsers
      if (dayjs(date).isValid()) {
        return dayjs(date).format('HH:mm');
      }
      // Fallback to native formatting with error handling
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    } catch (e) {
      console.warn('[Safari] Error formatting time:', e, date);
      return '';
    }
  };

  const formatDate = (date) => {
    // Safari-safe date formatting
    if (!date) return '';
    try {
      // Use dayjs for consistent formatting across browsers
      if (dayjs(date).isValid()) {
        return dayjs(date).format('ddd, DD MMM YYYY');
      }
      // Fallback to native formatting with error handling
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      return dateObj.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    } catch (e) {
      console.warn('[Safari] Error formatting date:', e, date);
      return '';
    }
  };

  if (loading && areaList.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', fontSize: '16px', color: '#6b7280' }}>
            Loading area schedule...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px 0' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            🏭 Area Plan Create
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Plan Version Dropdown */}
            <Dropdown
              menu={{
                items: planVersions.map((version) => ({
                  key: version.plan_version,
                  label: (
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontWeight: '500' }}>Version {version.plan_version}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {dayjs(version.published_at).format('DD-MMM-YYYY HH:mm')} • {version.record_count} records
                      </div>
                    </div>
                  ),
                })),
                onClick: ({ key }) => {
                  setSelectedVersion(Number(key));
                  message.info(`Switched to Version ${key}`);
                },
                selectable: true,
                selectedKeys: selectedVersion ? [String(selectedVersion)] : [],
              }}
              trigger={['click']}
              disabled={loadingVersions || planVersions.length === 0}
            >
              <Button>
                {loadingVersions ? (
                  <Spin size="small" />
                ) : selectedVersion ? (
                  <>Version {selectedVersion} <DownOutlined /></>
                ) : (
                  <>Select Version <DownOutlined /></>
                )}
              </Button>
            </Dropdown>
            {/* Publish Button */}
            <Button
              type="primary"
              style={{ backgroundColor: '#10b981' }}
              onClick={() => {
                Modal.confirm({
                  title: 'Publish Area Plan',
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      <p>Are you sure you want to publish the area plan?</p>
                      <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '8px' }}>
                        This will create a new version and make it available to all users.
                      </p>
                    </div>
                  ),
                  okText: 'Yes, Publish',
                  okButtonProps: { style: { backgroundColor: '#10b981' } },
                  cancelText: 'Cancel',
                  onOk: async () => {
                    try {
                      const response = await publishPlan();
                      if (response.success) {
                        message.success('Plan published successfully!');
                        // Refresh plan versions
                        const versionsResponse = await fetchPlanVersions();
                        setPlanVersions(versionsResponse.data || []);
                        if (versionsResponse.data && versionsResponse.data.length > 0) {
                          setSelectedVersion(versionsResponse.data[0].plan_version);
                        }
                      } else {
                        message.error(response.message || 'Failed to publish plan');
                      }
                    } catch (error) {
                      message.error('Failed to publish plan: ' + error.message);
                    }
                  },
                });
              }}
            >
              🚀 Publish
            </Button>
            <a 
              href="https://staging.nucleus.plus" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Staging Nucleus →
            </a>
          </div>
        </div>
        
        {/* Instructions */}
        <div style={{ 
          padding: '12px 16px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '14px', color: '#1e40af', fontWeight: '500' }}>
            💡 Click on any empty cell to create a new activity for that area and day
          </div>
        </div>
        
        {/* Date Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Select Date:</label>
            <DatePicker
              value={selectedDate}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date);
                }
              }}
              format="DD-MMM-YYYY"
              allowClear={false}
              style={{ width: 150 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            size="small"
              onClick={() => {
                const newDate = weekStart.subtract(1, 'week');
                setSelectedDate(newDate);
              }}
            style={{ fontSize: '13px' }}
          >
              ← Previous Week
          </Button>
            <Button 
              size="small"
              onClick={() => {
                const today = dayjs();
                const monday = today.startOf('week');
                setSelectedDate(monday);
              }}
              style={{ fontSize: '13px' }}
            >
              This Week
            </Button>
            <Button 
              size="small"
              onClick={() => {
                const newDate = weekStart.add(1, 'week');
                setSelectedDate(newDate);
              }}
              style={{ fontSize: '13px' }}
            >
              Next Week →
            </Button>
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            Showing week: {weekRangeText}
          </div>
          <Button
            size="small"
            onClick={handleExportAreaPlan}
            style={{
              fontSize: '11px',
              color: '#2563eb',
              borderColor: '#93c5fd',
              backgroundColor: 'transparent',
              padding: '0 8px',
              height: '22px',
              lineHeight: '20px',
              marginLeft: 'auto'
            }}
          >
            Export Area Plan
          </Button>
          {loadingActivities && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '14px' }}>
              <Spin size="small" />
              <span>Loading activities...</span>
            </div>
          )}
        </div>

        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '20px' }}>
            {activityTypes.map(type => (
              <div key={type.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: type.color,
                  borderRadius: '4px'
                }} />
                <span style={{ fontSize: '14px' }}>{type.label}</span>
              </div>
            ))}
          </div>
          
          {/* Delete Mode Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isDeleteMode ? (
              <>
                <span style={{ 
                  fontSize: '13px', 
                  color: '#ef4444',
                  fontWeight: '500'
                }}>
                  🗑️ Delete Mode: Click activities to select
                </span>
                <Button
                  size="small"
                  onClick={exitDeleteMode}
                  icon={<CloseOutlined />}
                  style={{ fontSize: '12px' }}
                >
                  Cancel
                </Button>
              </>
            ) : isReverseCascadeDeleteMode ? (
              <>
                <span style={{ 
                  fontSize: '13px', 
                  color: '#d97706',
                  fontWeight: '500'
                }}>
                  {reverseCascadeEquipment 
                    ? `⬅️ Delete & Cascade: ${allAreas.find(eq => eq.equipmentId === reverseCascadeEquipment)?.name || reverseCascadeEquipment} — Click activities to select`
                    : '⬅️ Delete & Cascade: Click any activity to lock area'
                  }
                </span>
                <Button
                  size="small"
                  onClick={exitReverseCascadeDeleteMode}
                  icon={<CloseOutlined />}
                  style={{ fontSize: '12px' }}
                >
                  Cancel
                </Button>
              </>
            ) : isClipboardMode ? (
              <>
                <span style={{ fontSize: '13px', color: '#c026d3', fontWeight: '500' }}>
                  {clipboardArea
                    ? `✂️ Cut / Copy: ${areaNameFor(clipboardArea)} — click activities to select`
                    : '✂️ Cut / Copy: click any activity to lock its area'
                  }
                </span>
                <Button
                  size="small"
                  onClick={exitClipboardMode}
                  icon={<CloseOutlined />}
                  style={{ fontSize: '12px' }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    setIsClipboardMode(true);
                    setIsDeleteMode(false);
                    setIsReverseCascadeDeleteMode(false);
                  }}
                  style={{
                    fontSize: '12px',
                    color: '#c026d3',
                    borderColor: '#f0abfc'
                  }}
                  disabled={!isLatestVersion || isHolding}
                  title={
                    !isLatestVersion
                      ? 'Can only move activities in the latest version'
                      : isHolding
                        ? 'Paste or cancel the current clipboard first'
                        : 'Select activities to cut or copy'
                  }
                >
                  ✂️ Cut / Copy
                </Button>
                <Button
                  size="small"
                  onClick={() => setIsDeleteMode(true)}
                  icon={<DeleteOutlined />}
                  style={{ 
                    fontSize: '12px',
                    color: '#ef4444',
                    borderColor: '#fca5a5'
                  }}
                  disabled={!isLatestVersion || isReverseCascadeDeleteMode}
                  title={!isLatestVersion ? 'Can only delete activities in latest version' : 'Enable multi-delete mode'}
                >
                  Multi-Delete
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setIsReverseCascadeDeleteMode(true);
                    setIsDeleteMode(false);
                  }}
                  icon={<DeleteOutlined />}
                  style={{ 
                    fontSize: '12px',
                    color: '#d97706',
                    borderColor: '#fbbf24'
                  }}
                  disabled={!isLatestVersion || isDeleteMode}
                  title={!isLatestVersion ? 'Can only delete activities in latest version' : 'Delete activities and shift subsequent ones backward'}
                >
                  Delete &amp; Cascade
                </Button>
                <Button
                  size="small"
                  onClick={() => setIsRndFilterMode(prev => !prev)}
                  style={{
                    fontSize: '12px',
                    color: isRndFilterMode ? '#fff' : '#7c3aed',
                    borderColor: '#a78bfa',
                    backgroundColor: isRndFilterMode ? '#7c3aed' : 'transparent',
                    fontWeight: isRndFilterMode ? '600' : '400'
                  }}
                  title={isRndFilterMode ? 'Click to show all batches' : 'Highlight R&D batches only'}
                >
                  {isRndFilterMode ? '🔬 R&D Filter ON' : '🔬 R&D Filter'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Cut / Copy Selection Bar */}
        {isClipboardMode && selectedForClipboard.size > 0 && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            zIndex: 1000
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {selectedForClipboard.size} {selectedForClipboard.size === 1 ? 'activity' : 'activities'} selected
            </span>
            <Button type="primary" onClick={() => handleHold('cut')}>
              Cut (Ctrl+X)
            </Button>
            <Button onClick={() => handleHold('copy')} style={{ color: 'white', borderColor: 'white' }} ghost>
              Copy (Ctrl+C)
            </Button>
            <Button
              onClick={() => { setSelectedForClipboard(new Set()); setClipboardArea(null); }}
              style={{ color: 'white', borderColor: 'white' }}
              ghost
            >
              Clear Selection
            </Button>
          </div>
        )}

        {/* Holding Bar - nothing has been written to the database yet */}
        {isHolding && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#a21caf',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            zIndex: 1000
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {clipboard.items.length} {clipboard.items.length === 1 ? 'activity' : 'activities'}{' '}
              {clipboard.op === 'cut' ? 'cut' : 'copied'} from {clipboard.sourceAreaName} — click an area to paste
            </span>
            <Button
              onClick={clearClipboard}
              icon={<CloseOutlined />}
              style={{ color: 'white', borderColor: 'white' }}
              ghost
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Delete Mode Selection Bar */}
        {isDeleteMode && selectedForDelete.size > 0 && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            zIndex: 1000
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {selectedForDelete.size} {selectedForDelete.size === 1 ? 'activity' : 'activities'} selected
            </span>
            <Button
              type="primary"
              danger
              onClick={handleMultiDelete}
              loading={deletingMultiple}
              icon={<DeleteOutlined />}
            >
              Delete Selected
            </Button>
            <Button
              onClick={() => setSelectedForDelete(new Set())}
              style={{ color: 'white', borderColor: 'white' }}
              ghost
            >
              Clear Selection
            </Button>
          </div>
        )}

        {/* Reverse Cascade Delete Selection Bar */}
        {isReverseCascadeDeleteMode && reverseCascadeEquipment && selectedForReverseCascadeDelete.size > 0 && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#78350f',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            zIndex: 1000
          }}>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              ⬅️ {selectedForReverseCascadeDelete.size} {selectedForReverseCascadeDelete.size === 1 ? 'activity' : 'activities'} selected on {allAreas.find(eq => eq.equipmentId === reverseCascadeEquipment)?.name || reverseCascadeEquipment}
            </span>
            <Button
              type="primary"
              danger
              onClick={handleReverseCascadeDelete}
              loading={deletingWithCascade}
              icon={<DeleteOutlined />}
              style={{ backgroundColor: '#d97706', borderColor: '#d97706' }}
            >
              Delete &amp; Shift Back
            </Button>
            <Button
              onClick={() => setSelectedForReverseCascadeDelete(new Set())}
              style={{ color: 'white', borderColor: 'white' }}
              ghost
            >
              Clear Selection
            </Button>
          </div>
        )}
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        {/* Sticky Header Row */}
        <div 
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          className="header-scroll-hidden"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            overflowX: 'auto',
            overflowY: 'visible'
          }}
        >
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #e5e7eb',
            width: '100%'
          }}>
            <div style={{ 
              width: `${AREA_LABEL_WIDTH}px`, 
              minWidth: `${AREA_LABEL_WIDTH}px`,
              padding: '12px',
              fontWeight: '600',
              backgroundColor: '#f9fafb',
              position: 'sticky',
              left: 0,
              zIndex: 110,
              borderRight: '2px solid #e5e7eb',
              flexShrink: 0
            }}>
              Area
            </div>
            <div style={{ display: 'flex', flex: 1 }}>
              {days.map(day => {
                const isToday = day.date.isSame(dayjs(), 'day');
                return (
              <div
                    key={day.index}
                style={{
                      flex: 1,
                      minWidth: 0,
                  boxSizing: 'border-box',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontSize: '11px',
                  fontWeight: '500',
                      backgroundColor: isToday ? '#fee2e2' : (day.index % 2 === 0 ? '#f9fafb' : '#ffffff'),
                      borderLeft: day.index === 0 ? 'none' : '1px solid #d1d5db',
                      borderRight: day.index === 6 ? '1px solid #d1d5db' : 'none'
                }}
              >
                    <div style={{ fontWeight: '600', color: isToday ? '#991b1b' : '#374151', marginBottom: '4px' }}>
                      {day.dayName}
                </div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>
                      {day.dayNumber} {day.month}
              </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scrollable Area Rows */}
        <div 
          ref={contentScrollRef}
          onScroll={handleContentScroll}
          className="content-scroll-hidden"
          style={{ overflowX: 'auto' }}
        >
          <div style={{ width: '100%', position: 'relative' }}>
            {/* Area Rows */}
            {allAreas.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                No area data available
              </div>
            ) : (
              allAreas.map((equipment) => {
                const eqBlocks = getActivityBlocks(equipment.id || equipment.name);
                const eqMaxRowIndex = eqBlocks.reduce((max, b) => Math.max(max, b.rowIndex || 0), 0);
                const eqRowHeight = 22;
                const eqPadding = 6;
                const eqDynamicHeight = Math.max(50, (eqMaxRowIndex + 1) * eqRowHeight + eqPadding * 2);
                
                return (
                <div
                  key={equipment.id}
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'relative',
                    minHeight: `${eqDynamicHeight}px`,
                    width: '100%'
                  }}
                >
                  {/* Area Name */}
                  <div style={{
                    width: `${AREA_LABEL_WIDTH}px`,
                    minWidth: `${AREA_LABEL_WIDTH}px`,
                    padding: '8px 12px',
                    fontWeight: '500',
                    fontSize: '13px',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    borderRight: '2px solid #e5e7eb',
                    flexShrink: 0
                  }}
                  title={equipment.areaName && equipment.areaName !== equipment.name
                    ? equipment.name + ' - ' + equipment.areaName
                    : equipment.name}>
                    {equipment.name}
                  </div>

                  {/* Day Blocks - Clickable cells */}
                  <div style={{ position: 'relative', flex: 1, display: 'flex', zIndex: 1 }}>
                    {days.map(day => {
                      const isToday = day.date.isSame(dayjs(), 'day');
                      const isPastDate = false;
                      return (
                      <div
                          key={day.index}
                          onClick={(e) => {
                            // Only handle click if not clicking on an activity card
                            if (!e.target.closest('[data-activity-card]')) {
                              !isPastDate && handleCellClick(equipment, day);
                            }
                          }}
                        style={{
                            flex: 1,
                            minWidth: 0,
                          boxSizing: 'border-box',
                            borderLeft: day.index === 0 ? 'none' : '1px solid #e5e7eb',
                            borderRight: day.index === 6 ? '1px solid #e5e7eb' : 'none',
                            backgroundColor: isPastDate ? '#f3f4f6' : (isToday ? '#fef2f2' : (day.index % 2 === 0 ? '#ffffff' : '#fafafa')),
                          cursor: isPastDate ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s',
                            opacity: isPastDate ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!isPastDate) {
                          e.currentTarget.style.backgroundColor = '#e0f2fe';
                          }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isPastDate ? '#f3f4f6' : (isToday ? '#fef2f2' : (day.index % 2 === 0 ? '#ffffff' : '#fafafa'));
                          }}
                          title={isPastDate ? `Past date - Cannot create activity` : `Click to add activity for ${equipment.name} on ${day.date.format('DD MMM YYYY')}`}
                        />
                      );
                    })}

                    {/* Activity Blocks */}
                    {(() => {
                      const equipmentIdentifier = equipment.id || equipment.name;
                      const blocks = getActivityBlocks(equipmentIdentifier);
                      if (blocks.length > 0) {
                        //console.log(`Rendering ${blocks.length} blocks for equipment: ${equipment.name} (id: ${equipment.id})`);
                      }
                      return blocks;
                    })().map(activity => {
                      const baseColor = activityColors[activity.activity_type] || '#6b7280';
                      
                      // Past-date lock removed — any day is editable
                      const isPastActivity = false;
                      
                      // Calculate vertical position for stacking (each row is ~22px height)
                      const rowHeight = 22;
                      const verticalOffset = (activity.rowIndex || 0) * rowHeight;
                      const cellHeight = 50; // Approximate cell height
                      const padding = 6;
                      const maxHeight = cellHeight - (padding * 2);
                      const availableHeight = maxHeight - verticalOffset;
                      const activityHeight = Math.min(rowHeight, Math.max(18, availableHeight));
                      
                      // Check if selected for delete
                      const isSelectedForDelete = selectedForDelete.has(activity.id);
                      // Clipboard flags. A held cut row is still in the database
                      // until the paste lands, so it stays on the grid, dashed.
                      const isSelectedForClipboard = selectedForClipboard.has(activity.id);
                      const isHeldForCut = heldIds.has(activity.id);
                      const isInClipboardWrongArea = isClipboardMode && clipboardArea && getEquipmentId(activity) !== clipboardArea;
                      const isSelectedForReverseCascade = selectedForReverseCascadeDelete.has(activity.id);
                      const isInReverseCascadeWrongEquipment = isReverseCascadeDeleteMode && reverseCascadeEquipment && getEquipmentId(activity) !== reverseCascadeEquipment;
                      const isAnyDeleteSelection = isSelectedForDelete || isSelectedForReverseCascade;
                      
                      // R&D filter mode flags
                      const isRndActivity = isRndFilterMode && isRndBatch(activity);
                      const isNonRndInFilterMode = isRndFilterMode && !isRndBatch(activity);
                      
                      return (
                        <div
                          key={activity.id}
                          data-activity-card="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            // In R&D filter mode, non-R&D blocks are not clickable
                            if (isNonRndInFilterMode) return;
                            if (isClipboardMode) {
                              // Past blocks are selectable, matching the rest of
                              // this screen ("Past-date lock removed"). Copying
                              // last week forward is a main use.
                              toggleClipboardSelection(activity);
                            } else if (isDeleteMode && !isPastActivity) {
                              // In delete mode, toggle selection
                              toggleActivitySelection(activity.id);
                            } else if (isReverseCascadeDeleteMode && !isPastActivity) {
                              // In reverse cascade delete mode
                              const actEquipmentId = getEquipmentId(activity);
                              if (!reverseCascadeEquipment) {
                                // First click — auto-lock to this equipment
                                setReverseCascadeEquipment(actEquipmentId);
                                toggleReverseCascadeSelection(activity.id);
                                const eqName = allAreas.find(eq => eq.equipmentId === actEquipmentId)?.name || actEquipmentId;
                                message.success(`Area "${eqName}" locked — select activities to delete`);
                              } else if (actEquipmentId === reverseCascadeEquipment) {
                                toggleReverseCascadeSelection(activity.id);
                              } else {
                                const lockedEqName = allAreas.find(eq => eq.equipmentId === reverseCascadeEquipment)?.name || reverseCascadeEquipment;
                                message.info(`Only activities on ${lockedEqName} can be selected`);
                              }
                            } else if (!isPastActivity && !isReverseCascadeDeleteMode) {
                              handleEditActivity(activity);
                            }
                          }}
                          style={{
                            position: 'absolute',
                            left: `${activity.leftPercent}%`,
                            width: `${activity.widthPercent}%`,
                            top: `${padding + verticalOffset}px`,
                            height: `${activityHeight}px`,
                            // Magenta is the only hue not already spoken for:
                            // blue = manufacturing, green = cleaning, amber =
                            // maintenance / cascade-delete, red = delete,
                            // violet = R&D. A blue selection was invisible
                            // against blue manufacturing blocks.
                            backgroundColor: isSelectedForReverseCascade ? '#d97706' : isSelectedForDelete ? '#ef4444' : isSelectedForClipboard ? '#c026d3' : baseColor,
                            borderRadius: '3px',
                            padding: '2px 4px',
                            color: 'white',
                            fontSize: '10px',
                            overflow: 'hidden',
                            boxShadow: isRndActivity
                              ? '0 0 0 2px #7c3aed, 0 2px 8px rgba(124,58,237,0.5)'
                              : isSelectedForReverseCascade
                                ? '0 0 0 2px #fbbf24, 0 2px 4px rgba(217,119,6,0.4)'
                                : isSelectedForDelete
                                  ? '0 0 0 2px #fca5a5, 0 2px 4px rgba(239,68,68,0.4)'
                                  : isSelectedForClipboard
                                    ? '0 0 0 3px #f5d0fe, 0 2px 8px rgba(192,38,211,0.55)'
                                    : '0 1px 2px rgba(0,0,0,0.2)',
                            // Marching ants: a held cut row is still in the
                            // database, so it stays visible but ghosted until
                            // the paste actually lands.
                            outline: isHeldForCut ? '2px dashed #c026d3' : 'none',
                            outlineOffset: isHeldForCut ? '1px' : 0,
                            cursor: isNonRndInFilterMode ? 'not-allowed' : isPastActivity ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: isRndActivity ? 20 : isAnyDeleteSelection || isSelectedForClipboard ? 15 : 10,
                            boxSizing: 'border-box',
                            pointerEvents: 'auto',
                            opacity: isNonRndInFilterMode ? 0.25 : isHeldForCut ? 0.45 : isPastActivity ? 0.6 : isInReverseCascadeWrongEquipment || isInClipboardWrongArea ? 0.35 : 1,
                            transition: 'all 0.15s ease'
                          }}
                          title={isClipboardMode
                            ? (isInClipboardWrongArea ? `Only ${areaNameFor(clipboardArea)} activities can be selected` : (isSelectedForClipboard ? 'Click to deselect' : 'Click to select for cut or copy'))
                            : isHeldForCut
                            ? 'Cut - still here until you paste'
                            : isDeleteMode
                            ? (isPastActivity ? 'Cannot delete past activities' : (isSelectedForDelete ? 'Click to deselect' : 'Click to select for deletion'))
                            : isReverseCascadeDeleteMode
                              ? (isPastActivity ? 'Cannot delete past activities' : isInReverseCascadeWrongEquipment ? `Only ${reverseCascadeEquipment} activities can be selected` : (isSelectedForReverseCascade ? 'Click to deselect' : 'Click to select for delete & cascade'))
                              : (isPastActivity ? `Past activity - Cannot edit` : `${activity.activity_type} - ${activity.batch_number || activity.cleaning_type || activity.maintenance_type || ''}${activity.end_time ? ` (${Math.round(dayjs(activity.end_time).diff(dayjs(activity.start_time), 'hour', true) * 10) / 10}hrs)` : ''} | ${dayjs(activity.start_time).format('DD MMM HH:mm')} - ${activity.end_time ? dayjs(activity.end_time).format('DD MMM HH:mm') : 'Ongoing'}`)
                          }
                          onMouseEnter={(e) => {
                            if (isNonRndInFilterMode) return;
                            e.currentTarget.style.zIndex = isRndActivity ? 20 : isAnyDeleteSelection ? 15 : 10;
                            e.currentTarget.style.boxShadow = isRndActivity
                              ? '0 0 0 3px #7c3aed, 0 4px 12px rgba(124,58,237,0.6)'
                              : isSelectedForReverseCascade
                                ? '0 0 0 3px #fbbf24, 0 4px 8px rgba(217,119,6,0.5)'
                                : isSelectedForDelete
                                  ? '0 0 0 3px #fca5a5, 0 4px 8px rgba(239,68,68,0.5)'
                                  : '0 2px 4px rgba(0,0,0,0.3)';
                          }}
                          onMouseLeave={(e) => {
                            if (isNonRndInFilterMode) return;
                            e.currentTarget.style.zIndex = isRndActivity ? 20 : isAnyDeleteSelection ? 15 : 5;
                            e.currentTarget.style.boxShadow = isRndActivity
                              ? '0 0 0 2px #7c3aed, 0 2px 8px rgba(124,58,237,0.5)'
                              : isSelectedForReverseCascade
                                ? '0 0 0 2px #fbbf24, 0 2px 4px rgba(217,119,6,0.4)'
                                : isSelectedForDelete
                                  ? '0 0 0 2px #fca5a5, 0 2px 4px rgba(239,68,68,0.4)'
                                  : '0 1px 2px rgba(0,0,0,0.2)';
                          }}
                        >
                          {/* Selection indicator for delete mode or reverse cascade mode */}
                          {(isDeleteMode || isClipboardMode || (isReverseCascadeDeleteMode && (!reverseCascadeEquipment || !isInReverseCascadeWrongEquipment))) && !isPastActivity && (
                            <div style={{
                              position: 'absolute',
                              top: '2px',
                              left: '2px',
                              width: '12px',
                              height: '12px',
                              borderRadius: '2px',
                              backgroundColor: (isSelectedForDelete || isSelectedForReverseCascade || isSelectedForClipboard) ? 'white' : 'rgba(255,255,255,0.3)',
                              border: '1px solid rgba(255,255,255,0.8)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px'
                            }}>
                              {(isSelectedForDelete || isSelectedForReverseCascade || isSelectedForClipboard) && '✓'}
                            </div>
                          )}
                          {/* Activity content */}
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '2px',
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                            overflow: 'hidden',
                            padding: '0 2px'
                          }}>
                            <div style={{ 
                              fontWeight: '600', 
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              fontSize: '10px',
                              lineHeight: '1.3',
                              textAlign: 'center',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {activity.batch_number || activity.cleaning_type || activity.maintenance_type || activity.activity_type}
                              {activity.end_time && <span style={{ fontSize: '8px', opacity: 0.85, marginLeft: '4px' }}>({Math.round(dayjs(activity.end_time).diff(dayjs(activity.start_time), 'hour', true) * 10) / 10}hrs)</span>}
                            </div>
                            <div style={{ 
                              fontSize: '8px', 
                              opacity: 0.9,
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              lineHeight: '1.3',
                              textAlign: 'center',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {dayjs(activity.start_time).format('DD MMM HH:mm')} - {activity.end_time ? dayjs(activity.end_time).format('DD MMM HH:mm') : 'Ongoing'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Fixed Horizontal Scrollbar at Bottom */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '24px',
          backgroundColor: '#e5e7eb',
          borderTop: '2px solid #cbd5e1',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px'
        }}
      >
        <div
          ref={fixedScrollRef}
          onScroll={handleFixedScroll}
          className="visible-scrollbar"
          style={{
            flex: 1,
            overflowX: 'scroll',
            overflowY: 'hidden'
          }}
        >
          <div style={{ width: '2000px', height: '1px' }} />
        </div>
      </div>

      {/* Scrollbar styles */}
      <style>
        {`
          .header-scroll-hidden,
          .content-scroll-hidden {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .header-scroll-hidden::-webkit-scrollbar,
          .content-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
          
          .visible-scrollbar {
            scrollbar-width: auto !important;
            scrollbar-color: #64748b #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar {
            height: 16px !important;
            display: block !important;
          }
          .visible-scrollbar::-webkit-scrollbar-track {
            background: #d1d5db !important;
            border-radius: 8px !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb {
            background: #64748b !important;
            border-radius: 8px !important;
            border: 3px solid #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #475569 !important;
          }
          
          /* Custom TimePicker - Show 12 after 11 (reorder hours: 1-11, then 12) */
          /* Target TimePicker dropdown globally - hours are in order: 12, 1, 2... 11 */
          /* We want: 1, 2, 3... 11, 12 */
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child {
            display: flex;
            flex-direction: column;
          }
          /* Hour 12 (first child, index 0) should be last */
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(1) {
            order: 12;
          }
          /* Hours 1-11 (children 2-12) should be in order 1-11 */
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(2) { order: 1; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(3) { order: 2; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(4) { order: 3; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(5) { order: 4; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(6) { order: 5; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(7) { order: 6; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(8) { order: 7; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(9) { order: 8; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(10) { order: 9; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(11) { order: 10; }
          .ant-picker-dropdown .ant-picker-time-panel-column:first-child .ant-picker-time-panel-cell:nth-child(12) { order: 11; }
        `}
      </style>
      
      {/* Bottom padding for fixed scrollbar */}
      <div style={{ height: '25px' }} />

      {/* Create Activity Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '8px', 
              height: '24px', 
              backgroundColor: getActivityColor(formData.activity_type),
              borderRadius: '4px'
            }} />
            <span>{editingActivityId ? 'Edit Activity' : 'Create New Activity'}</span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingActivityId(null);
          handleReset();
        }}
        footer={null}
        // Phone: a full-height sheet pinned to the top, like a mobile page.
        // Anything wider: 600px, but never wider than the window, so tablets
        // and small/resized desktop windows never overflow.
        width={isNarrowScreen ? '100vw' : 'min(600px, 94vw)'}
        style={isNarrowScreen ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } : { top: 20 }}
        // The body scrolls, never the page, so Create/Cancel stay reachable at
        // any height. 150px covers the title bar plus the sticky action row.
        styles={{
          body: {
            maxHeight: isNarrowScreen ? 'calc(100vh - 110px)' : 'calc(100vh - 150px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: isNarrowScreen ? '8px 12px' : '8px 16px',
          },
        }}
        destroyOnClose
      >
        <ConfigProvider componentSize="small">
        {/* Responsive rules live in CSS, not in a JS breakpoint, so the form
            reflows continuously at any width instead of snapping at one size.
            apc-row  = a pair of fields that stacks when there is no room
            apc-seg  = a row of choice buttons that share the width equally */}
        <style>{`
          .apc-form .apc-row { display: flex; gap: 10px; align-items: flex-start; }
          .apc-form .apc-row > * { flex: 1 1 0; min-width: 0; }
          .apc-form .apc-seg { display: flex; gap: 6px; }
          .apc-form .apc-seg > * { flex: 1 1 0; min-width: 0; justify-content: center; }
          .apc-form label { line-height: 1.3; }

          /* Tablet and below: choice buttons get a little more breathing room
             than a phone but still share the row. */
          @media (max-width: 768px) {
            .apc-form .apc-row { gap: 8px; }
          }
          /* Phones: one field per line - half of a 360px screen cannot hold a
             date picker, a product name or a batch code. */
          @media (max-width: 575px) {
            .apc-form .apc-row { flex-direction: column; gap: 6px; }
            /* "Manufacturing" will not fit in a third of a 360px screen, so
               the choices wrap to two per line and lose side padding. */
            .apc-form .apc-seg { flex-wrap: wrap; }
            .apc-form .apc-seg > * {
              flex: 1 1 40%;
              padding-left: 8px !important;
              padding-right: 8px !important;
              overflow: hidden;
            }
          }
          /* Short viewports (landscape phone, split screen): trim the gaps
             rather than the field sizes, since height is what runs out. */
          @media (max-height: 560px) {
            .apc-form .apc-row { gap: 6px; }
            .apc-form > div { margin-bottom: 6px !important; }
          }
        `}</style>
        <div className="apc-form" style={{ padding: '2px 0' }}>
          {/* Selected Cell Info */}
          {/* One line, not a two-column card - the labels were costing more
              height than the values they described. */}
          {selectedCell && (
            <div style={{
              padding: '5px 10px',
              backgroundColor: '#f3f4f6',
              borderRadius: '6px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#1f2937',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              <span style={{ color: '#6b7280', fontWeight: '400' }}>Area</span>
              <span>{selectedCell.equipment.name}</span>
              <span style={{ color: '#d1d5db', fontWeight: '400' }}>|</span>
              <span style={{ color: '#6b7280', fontWeight: '400' }}>Time Slot</span>
              <span>{dayjs(selectedCell.cellTime).format('DD-MMM-YY HH:mm')}</span>
            </div>
          )}

          {/* Activity Type Selection */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', color: '#374151', fontSize: '11px' }}>
              Activity Type {!editingActivityId && '*'}
            </label>
            <div className="apc-seg">
              {activityTypes.map(type => (
                <div
                  key={type.value}
                  onClick={() => !editingActivityId && handleChange('activity_type', type.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    cursor: editingActivityId ? 'not-allowed' : 'pointer',
                    border: formData.activity_type === type.value ? `2px solid ${type.color}` : '2px solid #e5e7eb',
                    backgroundColor: editingActivityId ? '#f3f4f6' : (formData.activity_type === type.value ? `${type.color}15` : 'white'),
                    opacity: editingActivityId ? 0.6 : 1,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ width: '14px', height: '14px', borderRadius: '4px', backgroundColor: type.color }} />
                  <span style={{ 
                    fontWeight: formData.activity_type === type.value ? '600' : '500',
                    color: formData.activity_type === type.value ? type.color : '#374151',
                    fontSize: '13px'
                  }}>
                    {type.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Start & End Date Time - Two Separate Cards.
              Side by side on a laptop; stacked on a phone, where squeezing a
              date and a time picker into half a narrow screen is unusable. */}
          <div style={{ marginBottom: '8px' }}>
            <div className="apc-row">
              {/* Starts Card */}
              <div style={{ 
                flex: 1,
                minWidth: 0,
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  fontSize: '12px' 
                }}>
                  Starts
                </label>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
              <DatePicker
                    format="DD MMM YYYY"
                    value={
                      formData.start_date 
                        ? (dayjs.isDayjs(formData.start_date) ? formData.start_date : dayjs(formData.start_date))
                        : (formData.start_time 
                            ? (dayjs.isDayjs(formData.start_time) ? formData.start_time : dayjs(formData.start_time))
                            : null)
                    }
                    onChange={(value) => handleChange('start_date', value)}
                    disabled={false}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '100px',
                      maxWidth: '100%',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      height: '32px'
                    }}
                    placeholder="Date"
                    allowClear={false}
                  />
                  <TimePicker
                      format="HH:mm"
                      value={
                        formData.start_time_only !== null && formData.start_time_only !== undefined
                          ? (dayjs.isDayjs(formData.start_time_only) 
                              ? formData.start_time_only 
                              : (formData.start_time_only ? dayjs(formData.start_time_only) : null))
                          : (formData.start_time !== null && formData.start_time !== undefined
                              ? (dayjs.isDayjs(formData.start_time) 
                                  ? formData.start_time 
                                  : (formData.start_time ? dayjs(formData.start_time) : null))
                              : null)
                      }
                      onChange={(value) => handleChange('start_time_only', value)}
                      disabled={false}
                      minuteStep={5}
                      style={{
                        flex: '0 0 auto',
                        width: '75px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        fontSize: '12px',
                        height: '32px'
                      }}
                      placeholder="Time"
                      getPopupContainer={(trigger) => trigger.parentElement}
                      popupStyle={{ zIndex: 1050 }}
                      allowClear={false}
              />
            </div>
              </div>

              {/* Ends Card */}
              <div style={{ 
                flex: 1,
                minWidth: 0,
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  fontSize: '12px' 
                }}>
                  Ends
              </label>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
              <DatePicker
                    format="DD MMM YYYY"
                    value={
                      formData.end_date
                        ? (dayjs.isDayjs(formData.end_date) ? formData.end_date : dayjs(formData.end_date))
                        : (formData.end_time
                            ? (dayjs.isDayjs(formData.end_time) ? formData.end_time : dayjs(formData.end_time))
                            : null)
                    }
                    onChange={(value) => handleChange('end_date', value)}
                    // disabled={!!formData.duration_hrs}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '100px',
                      maxWidth: '100%',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      height: '32px'
                    }}
                    placeholder="Date"
                    allowClear={false}
                  />
                  <TimePicker
                      format="HH:mm"
                      value={
                        formData.end_time_only !== null && formData.end_time_only !== undefined
                          ? (dayjs.isDayjs(formData.end_time_only) 
                              ? formData.end_time_only 
                              : (formData.end_time_only ? dayjs(formData.end_time_only) : null))
                          : (formData.end_time !== null && formData.end_time !== undefined
                              ? (dayjs.isDayjs(formData.end_time) 
                                  ? formData.end_time 
                                  : (formData.end_time ? dayjs(formData.end_time) : null))
                              : null)
                      }
                      onChange={(value) => handleChange('end_time_only', value)}
                      // disabled={!!formData.duration_hrs}
                      minuteStep={5}
                      style={{
                        flex: '0 0 auto',
                        width: '75px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        fontSize: '12px',
                        height: '32px'
                      }}
                      placeholder="Time"
                      getPopupContainer={(trigger) => trigger.parentElement}
                      popupStyle={{ zIndex: 1050 }}
                      allowClear={false}
                    />
                </div>
              </div>
            </div>
            
            {/* Duration Display */}
            {formData.start_time && formData.end_time && (
              <div style={{ 
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#ecfdf5',
                borderRadius: '6px',
                border: '1px solid #a7f3d0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Duration:</span>
                <span style={{ fontWeight: '600', color: '#059669', fontSize: '13px' }}>
                  {(() => {
                    const start = dayjs.isDayjs(formData.start_time) ? formData.start_time : dayjs(formData.start_time);
                    const end = dayjs.isDayjs(formData.end_time) ? formData.end_time : dayjs(formData.end_time);
                    return `${Math.round(end.diff(start, 'hour', true) * 10) / 10} hrs`;
                  })()}
                </span>
              </div>
            )}
          </div>

         

          {/* Manufacturing Fields */}
          {/* No tinted panel - the border, background and padding cost ~30px
              of height and the fields read fine without a box around them. */}
          {formData.activity_type === 'manufacturing' && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>

              {/* Scheduling Mode Selection - Only show when not editing */}
              {!editingActivityId && (
                <div>
                  {/* The mode description rides on the label's own line
                      instead of taking a line of its own underneath. */}
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', color: '#374151', fontSize: '11px' }}>
                    Scheduling Mode
                    <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '6px' }}>
                      {formData.scheduling_mode === 'auto'
                        ? '— auto-generate batch numbers from starting batch'
                        : '— manually enter each batch number and duration'}
                    </span>
                  </label>
                  <div className="apc-seg">
                    <div
                      onClick={() => handleChange('scheduling_mode', 'auto')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: formData.scheduling_mode === 'auto' ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                        backgroundColor: formData.scheduling_mode === 'auto' ? '#dbeafe' : 'white',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>🤖</span>
                      <span style={{ 
                        fontWeight: formData.scheduling_mode === 'auto' ? '600' : '500',
                        color: formData.scheduling_mode === 'auto' ? '#1d4ed8' : '#374151',
                        fontSize: '13px'
                      }}>
                        Auto Scheduling
                      </span>
                    </div>
                    <div
                      onClick={() => handleChange('scheduling_mode', 'manual')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: formData.scheduling_mode === 'manual' ? '2px solid #f59e0b' : '2px solid #e5e7eb',
                        backgroundColor: formData.scheduling_mode === 'manual' ? '#fef3c7' : 'white',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>✏️</span>
                      <span style={{ 
                        fontWeight: formData.scheduling_mode === 'manual' ? '600' : '500',
                        color: formData.scheduling_mode === 'manual' ? '#b45309' : '#374151',
                        fontSize: '13px'
                      }}>
                        Manual Scheduling
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Product and batch share one row - two short fields
                  do not each need the full width of the form. */}
              <div className="apc-row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    Product Name *
                  </label>
                  <Select
                    placeholder="Select product name"
                    value={formData.product_name || undefined}
                    onChange={(value) => {
                      handleChange('product_name', value);
                      // Clear volume option when product changes
                      handleChange('volume_option', null);
                      handleChange('volume_option_payload', null);
                      handleChange('volume_option_data', null);
                      setSelectedVolumeOption(null);
                      // Clear option mismatch warning when product changes
                      setOptionMismatchWarning(false);
                      setOriginalOptionSelected(null);
                      // Fetch new volume options
                      if (value) {
                        setLoadingVolumeOptions(true);
                        fetchProductVolumeOptions(value)
                          .then(response => {
                            setVolumeOptions(response.data?.options || []);
                          })
                          .catch(err => {
                            console.error('Error loading volume options:', err);
                            setVolumeOptions([]);
                          })
                          .finally(() => setLoadingVolumeOptions(false));
                      } else {
                        setVolumeOptions([]);
                      }
                    }}
                    options={availableProducts}
                    loading={loadingProducts}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                  />
                </div>
                {formData.scheduling_mode === 'auto' && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    {formData.number_of_batches > 0 ? 'Starting Batch Number' : 'Batch Number'} *
                  </label>
                  <Input
                    placeholder={formData.number_of_batches > 0 ? "e.g. CGTCB26001" : "Enter batch number"}
                    value={formData.batch_number}
                    onChange={(e) => handleChange('batch_number', e.target.value)}
                  />
                  {formData.number_of_batches > 0 && formData.batch_number && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px', 
                      backgroundColor: '#f0fdf4', 
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#15803d'
                    }}>
                      <strong>Generated batch numbers:</strong>
                      <div style={{ marginTop: '4px' }}>
                        {(() => {
                          const batches = [];
                          // Extract numeric part from batch number
                          const match = formData.batch_number.match(/^([A-Za-z]+)?(\d+)$/);
                          if (match) {
                            const prefix = match[1] || '';
                            const startNum = parseInt(match[2]);
                            for (let i = 0; i < formData.number_of_batches; i++) {
                              batches.push(`${prefix}${String(startNum + i).padStart(match[2].length, '0')}`);
                            }
                          }
                          return batches.slice(0, 5).join(', ') + (batches.length > 5 ? ` ... (${batches.length} total)` : '');
                        })()}
                      </div>
                    </div>
                  )}
                  </div>
                )}
              </div>

              {/* Volume Option Dropdown - shown when product is selected */}
              {formData.product_name && (
                <div>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    Volume Option
                  </label>
                  {/* Warning for mismatched/outdated option */}
                  {optionMismatchWarning && originalOptionSelected && (
                    <Alert
                      message="Option may be outdated"
                      description={`The previously selected option "${originalOptionSelected}" may no longer be available. Please re-select an option from the dropdown below.`}
                      type="warning"
                      showIcon
                      style={{ marginBottom: '5px' }}
                      closable
                      onClose={() => {
                        setOptionMismatchWarning(false);
                        setOriginalOptionSelected(null);
                      }}
                    />
                  )}
                  <Select
                    placeholder="Select volume option"
                    value={formData.volume_option || undefined}
                    onChange={(value) => {
                      // Find the selected option
                      const optionData = processedVolumeOptions.find(opt => opt.value === value);
                      // Store the display value (combined label) for Select display
                      handleChange('volume_option', value);
                      // Store the original value for API payload (use originalValue if combined, else use value)
                      handleChange('volume_option_payload', optionData?.originalValue || value);
                      handleChange('volume_option_data', optionData || null);
                      setSelectedVolumeOption(optionData || null);
                      // Clear warning when user selects a new option
                      setOptionMismatchWarning(false);
                      setOriginalOptionSelected(null);
                    }}
                    options={processedVolumeOptions}
                    loading={loadingVolumeOptions}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                  />
                  {selectedVolumeOption && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 12px', 
                      backgroundColor: '#f0f9ff', 
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#0369a1',
                      border: '1px solid #bae6fd'
                    }}>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <span><strong>Batches:</strong> {selectedVolumeOption.num_batches}</span>
                        {currentEquipmentCategory === 'granulation' ? (
                          <span><strong>Volume:</strong> {selectedVolumeOption.volume_gran_fbp_blend}</span>
                        ) : currentEquipmentCategory === 'compression' ? (
                          <span><strong>Volume:</strong> {selectedVolumeOption.volume_comp_coat_cap_imp}</span>
                        ) : (
                          <>
                            <span><strong>Gran/FBP/Blend Vol:</strong> {selectedVolumeOption.volume_gran_fbp_blend}</span>
                            <span><strong>Comp/Coat/Cap/Imp Vol:</strong> {selectedVolumeOption.volume_comp_coat_cap_imp}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Auto Scheduling Mode Fields */}
              {formData.scheduling_mode === 'auto' && (
                <>

                {/* Batch Processing Time & Number of Batches - Side by Side */}
                <div className="apc-row">
                  {/* Batch Processing Time */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', color: '#374151', fontSize: '11px' }}>
                      Batch Processing Time (hrs) *
                      <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '6px' }}>
                        — end time auto-calculates
                      </span>
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.25"
                      placeholder="e.g. 2.5"
                      value={formData.duration_hrs}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleChange('duration_hrs', v);
                        recalcEndFromDuration(v, formData.start_time);
                      }}
                    />
                  </div>

                  {/* Number of Batches */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                      Number of Batches
                    </label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0 for single batch"
                      value={formData.number_of_batches}
                      onChange={(e) => handleChange('number_of_batches', parseInt(e.target.value) || 0)}
                    />
                    <div style={{ 
                      marginTop: '6px',
                      fontSize: '12px', 
                      color: '#6b7280'
                    }}>
                      {formData.number_of_batches > 0
                        ? `Will create ${formData.number_of_batches} batch(es)`
                        : 'Enter 0 for single batch'}
                    </div>
                  </div>
                </div>

          {/* Cleaning Between Batches - Only show when number_of_batches > 0 */}
          {formData.number_of_batches > 0 && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f0fdf4',
              borderRadius: '6px',
              border: '1px solid #86efac',
              marginTop: '12px'
            }}>
              <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                ✓ Cleaning Between Batches (Optional)
              </label>
              
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                    Cleaning Type
                  </label>
                  <Select
                    placeholder="Select cleaning type"
                    value={formData.cleaning_type_between}
                    onChange={(value) => handleChange('cleaning_type_between', value)}
                    style={{ width: '100%', maxWidth: '250px' }}
                    options={cleaningTypes}
                    allowClear
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                    Cleaning Duration (hrs)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.25"
                    placeholder="e.g. 0.5"
                    value={formData.cleaning_duration_between}
                    onChange={(e) => handleChange('cleaning_duration_between', e.target.value)}
                    style={{ maxWidth: '250px' }}
                  />
                </div>
              </div>

              {/* Schedule Preview */}
              {formData.batch_number && formData.number_of_batches > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#374151'
                }}>
                  <strong>📅 Schedule Preview:</strong>
                  {/* Every batch and cleaning line is kept. The list scrolls
                      inside a fixed height so a 20-batch plan cannot push the
                      rest of the form off the screen. */}
                  <div style={{ marginTop: '4px', lineHeight: '1.45', maxHeight: '160px', overflowY: 'auto' }}>
                    {(() => {
                      const batches = [];
                      const match = formData.batch_number.match(/^([A-Za-z]+)?(\d+)$/);
                      if (match) {
                        const prefix = match[1] || '';
                        const startNum = parseInt(match[2]);
                        for (let i = 0; i < formData.number_of_batches; i++) {
                          const batchNum = String(startNum + i).padStart(match[2].length, '0');
                          batches.push(`${prefix}${batchNum}`);
                        }
                      }

                      return batches.map((batch, idx) => (
                        <div key={idx}>
                          <div style={{ color: '#3b82f6', fontWeight: '500' }}>↳ Batch {idx + 1}: <strong>{batch}</strong></div>
                          {idx < batches.length - 1 && formData.cleaning_type_between && (
                            <div style={{ color: '#10b981', fontWeight: '500', marginLeft: '20px' }}>
                              → Cleaning: {formData.cleaning_type_between} {formData.cleaning_duration_between ? `(${formData.cleaning_duration_between} hrs)` : ''}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
                </>
              )}

              {/* Manual Scheduling Mode Fields */}
              {formData.scheduling_mode === 'manual' && !editingActivityId && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '6px', 
                  border: '1px solid #fcd34d' 
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '11px' }}>
                    ✏️ Manual Batch Entries
                  </label>
                  
                  {formData.manual_batches.map((batch, index) => {
                    // Calculate start time for this batch
                    let batchStartTime = formData.start_time;
                    if (index > 0) {
                      // Calculate cumulative start time based on previous batches
                      let cumulativeTime = dayjs(formData.start_time);
                      for (let i = 0; i < index; i++) {
                        const prevBatch = formData.manual_batches[i];
                        const prevDuration = Number(prevBatch.duration_hrs) || 0;
                        cumulativeTime = cumulativeTime.add(prevDuration, 'hour');
                        // Add cleaning time if present
                        if (prevBatch.cleaning_type && prevBatch.cleaning_duration_hrs) {
                          const cleaningDuration = Number(prevBatch.cleaning_duration_hrs) || 0;
                          cumulativeTime = cumulativeTime.add(cleaningDuration, 'hour');
                        }
                      }
                      batchStartTime = cumulativeTime;
                    }
                    
                    // Calculate end time for this batch
                    const batchDuration = Number(batch.duration_hrs) || 0;
                    const batchEndTime = batchDuration > 0 ? dayjs(batchStartTime).add(batchDuration, 'hour') : null;
                    
                    return (
                      <div key={index} style={{ 
                        marginBottom: index < formData.manual_batches.length - 1 ? '16px' : '0',
                        padding: '12px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: '8px' 
                        }}>
                          <span style={{ fontWeight: '600', color: '#374151', fontSize: '13px' }}>
                            Batch {index + 1}
                          </span>
                          {formData.manual_batches.length > 1 && (
                            <Button 
                              size="small" 
                              danger
                              onClick={() => {
                                const newBatches = formData.manual_batches.filter((_, i) => i !== index);
                                handleChange('manual_batches', newBatches);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        
                        {/* Batch Number */}
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                            Batch Number *
                          </label>
                          <Input
                            placeholder="Enter batch number"
                            value={batch.batch_number}
                            onChange={(e) => {
                              const newBatches = [...formData.manual_batches];
                              newBatches[index] = { ...newBatches[index], batch_number: e.target.value };
                              handleChange('manual_batches', newBatches);
                            }}
                          />
                        </div>

                        {/* Product Name - prefilled but editable */}
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                            Product Name *
                          </label>
                          <Select
                            placeholder="Select product name"
                            value={batch.product_name || formData.product_name || undefined}
                            onChange={(value) => {
                              const newBatches = [...formData.manual_batches];
                              newBatches[index] = { 
                                ...newBatches[index], 
                                product_name: value,
                                volume_option: null, // Clear volume option when product changes
                                volume_option_data: null
                              };
                              handleChange('manual_batches', newBatches);
                            }}
                            options={availableProducts}
                            loading={loadingProducts}
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: '100%' }}
                            size="small"
                          />
                        </div>

                        {/* Volume Option - shown when product is selected */}
                        {(batch.product_name || formData.product_name) && (
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                              Volume Option
                            </label>
                            <Select
                              placeholder="Select volume option"
                              value={batch.volume_option || undefined}
                              onChange={(value) => {
                                const newBatches = [...formData.manual_batches];
                                const optionData = processedVolumeOptions.find(opt => opt.value === value);
                                // Store display value for Select, and payload value separately
                                newBatches[index] = { 
                                  ...newBatches[index], 
                                  volume_option: value, // Display value
                                  volume_option_payload: optionData?.originalValue || value, // Payload value
                                  volume_option_data: optionData || null
                                };
                                handleChange('manual_batches', newBatches);
                              }}
                              options={processedVolumeOptions}
                              loading={loadingVolumeOptions}
                              showSearch
                              allowClear
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              style={{ width: '100%' }}
                              size="small"
                            />
                            {batch.volume_option_data && (
                              <div style={{ 
                                marginTop: '4px', 
                                padding: '6px 8px', 
                                backgroundColor: '#f0f9ff', 
                                borderRadius: '4px',
                                fontSize: '11px',
                                color: '#0369a1'
                              }}>
                                <span><strong>Batches:</strong> {batch.volume_option_data.num_batches} | </span>
                                {currentEquipmentCategory === 'granulation' ? (
                                  <span><strong>Vol:</strong> {batch.volume_option_data.volume_gran_fbp_blend}</span>
                                ) : currentEquipmentCategory === 'compression' ? (
                                  <span><strong>Vol:</strong> {batch.volume_option_data.volume_comp_coat_cap_imp}</span>
                                ) : (
                                  <>
                                    <span><strong>Gran Vol:</strong> {batch.volume_option_data.volume_gran_fbp_blend} | </span>
                                    <span><strong>Comp Vol:</strong> {batch.volume_option_data.volume_comp_coat_cap_imp}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Duration & Calculated End Time */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                              Duration (hrs) *
                            </label>
                            <Input
                              type="number"
                              min={0}
                              step="0.25"
                              placeholder="e.g. 2.5"
                              value={batch.duration_hrs}
                              onChange={(e) => {
                                const newBatches = [...formData.manual_batches];
                                newBatches[index] = { ...newBatches[index], duration_hrs: e.target.value };
                                handleChange('manual_batches', newBatches);
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#6b7280', fontSize: '12px' }}>
                              End Time (calculated)
                            </label>
                            <div style={{ 
                              padding: '8px 12px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px', 
                              fontSize: '13px',
                              color: '#374151',
                              border: '1px solid #d1d5db',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {batchEndTime ? batchEndTime.format('DD-MMM HH:mm') : '--'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Next Cleaning (not for last batch) */}
                        {index < formData.manual_batches.length - 1 && (
                          <div style={{ 
                            padding: '8px', 
                            backgroundColor: '#f0fdf4', 
                            borderRadius: '4px',
                            border: '1px solid #86efac'
                          }}>
                            <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#15803d', fontSize: '12px' }}>
                              🧹 Next Cleaning (before Batch {index + 2})
                            </label>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                                  Cleaning Type
                                </label>
                                <Select
                                  placeholder="Cleaning Type"
                                  value={batch.cleaning_type}
                                  onChange={(value) => {
                                    const newBatches = [...formData.manual_batches];
                                    newBatches[index] = { ...newBatches[index], cleaning_type: value };
                                    handleChange('manual_batches', newBatches);
                                  }}
                                  style={{ width: '100%' }}
                                  options={cleaningTypes}
                                  allowClear
                                  size="small"
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                                  Duration (hrs)
                                </label>
                                <Input
                                  type="number"
                                  min={0}
                                  step="0.25"
                                  placeholder="Duration (hrs)"
                                  value={batch.cleaning_duration_hrs}
                                  onChange={(e) => {
                                    const newBatches = [...formData.manual_batches];
                                    newBatches[index] = { ...newBatches[index], cleaning_duration_hrs: e.target.value };
                                    handleChange('manual_batches', newBatches);
                                  }}
                                  size="small"
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#6b7280', fontSize: '11px' }}>
                                  End Time (calculated)
                                </label>
                                <div style={{ 
                                  padding: '4px 8px', 
                                  backgroundColor: '#dcfce7', 
                                  borderRadius: '4px', 
                                  fontSize: '12px',
                                  color: '#166534',
                                  border: '1px solid #86efac',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {(() => {
                                    const cleaningDuration = Number(batch.cleaning_duration_hrs) || 0;
                                    if (batchEndTime && cleaningDuration > 0) {
                                      return batchEndTime.add(cleaningDuration, 'hour').format('DD-MMM HH:mm');
                                    }
                                    return '--';
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Add Next Batch Button */}
                  <Button
                    type="dashed"
                    onClick={() => {
                      const newBatches = [...formData.manual_batches, {
                        batch_number: '',
                        product_name: formData.product_name || '',
                        duration_hrs: '',
                        cleaning_type: null,
                        cleaning_duration_hrs: '',
                        end_time: null
                      }];
                      handleChange('manual_batches', newBatches);
                    }}
                    style={{ 
                      width: '100%', 
                      marginTop: '12px',
                      borderColor: '#f59e0b',
                      color: '#b45309'
                    }}
                  >
                    + Add Next Batch
                  </Button>
                  
                  {/* Schedule Preview for Manual Mode */}
                  {formData.manual_batches.some(b => b.batch_number && b.duration_hrs) && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#374151',
                      border: '1px solid #e5e7eb'
                    }}>
                      <strong>📅 Schedule Preview:</strong>
                      {/* Same as the auto-mode preview: nothing dropped, the
                          list just scrolls inside a fixed height. */}
                      <div style={{ marginTop: '4px', lineHeight: '1.45', maxHeight: '160px', overflowY: 'auto' }}>
                        {(() => {
                          let currentTime = dayjs(formData.start_time);
                          const validBatches = formData.manual_batches.filter(b => b.batch_number && b.duration_hrs);
                          
                          return validBatches.map((batch, idx) => {
                            const duration = Number(batch.duration_hrs) || 0;
                            const startTime = currentTime;
                            const endTime = currentTime.add(duration, 'hour');
                            currentTime = endTime;
                            const batchProductName = batch.product_name || formData.product_name || '';
                            
                            const hasCleaningAfter = batch.cleaning_type && batch.cleaning_duration_hrs && idx < validBatches.length - 1;
                            if (hasCleaningAfter) {
                              const cleaningDuration = Number(batch.cleaning_duration_hrs) || 0;
                              currentTime = currentTime.add(cleaningDuration, 'hour');
                            }
                            
                            return (
                              <div key={idx}>
                                <div style={{ color: '#3b82f6', fontWeight: '500' }}>
                                  ↳ Batch {idx + 1}: <strong>{batch.batch_number}</strong> {batchProductName && <span style={{ color: '#6b7280', fontWeight: '400' }}>({batchProductName})</span>} ({startTime.format('HH:mm')} - {endTime.format('HH:mm')})
                                </div>
                                {hasCleaningAfter && (
                                  <div style={{ color: '#10b981', fontWeight: '500', marginLeft: '20px' }}>
                                    → Cleaning: {batch.cleaning_type} ({batch.cleaning_duration_hrs} hrs)
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* For editing mode, show simple batch fields */}
              {editingActivityId && (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                      Batch Number *
                    </label>
                    <Input
                      placeholder="Enter batch number"
                      value={formData.batch_number}
                      onChange={(e) => handleChange('batch_number', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', color: '#374151', fontSize: '11px' }}>
                      Batch Processing Time (hrs) *
                      <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '6px' }}>
                        — end time auto-calculates
                      </span>
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.25"
                      placeholder="e.g. 2.5"
                      value={formData.duration_hrs}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleChange('duration_hrs', v);
                        recalcEndFromDuration(v, formData.start_time);
                      }}
                    />
                  </div>
                </>
              )}
              </div>
            </div>
          )}

          {/* Cleaning Fields */}
          {formData.activity_type === 'cleaning' && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#ecfdf5', 
              borderRadius: '8px', 
              marginBottom: '8px',
              border: '1px solid #a7f3d0'
            }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    Activity Type *
                  </label>
                  <Select
                    placeholder="Select activity type"
                    value={formData.cleaning_type}
                    onChange={(value) => handleChange('cleaning_type', value)}
                    style={{ width: '100%' }}
                    options={cleaningTypes}
                  />
                </div>
                <div style={{ flex: '1', minWidth: '150px', maxWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    Duration (hrs) *
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.25"
                    placeholder="e.g. 2.5"
                    value={formData.duration_hrs}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('duration_hrs', v);
                      recalcEndFromDuration(v, formData.start_time);
                    }}
                  />
                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                    End time auto-calculates
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance Fields */}
          {formData.activity_type === 'maintenance' && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fffbeb', 
              borderRadius: '8px', 
              marginBottom: '8px',
              border: '1px solid #fde68a'
            }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    Maintenance Type *
                  </label>
                  <Select
                    placeholder="Select maintenance type"
                    value={formData.maintenance_type}
                    onChange={(value) => handleChange('maintenance_type', value)}
                    style={{ width: '100%' }}
                    options={maintenanceTypes}
                  />
                </div>
                <div style={{ flex: '1', minWidth: '150px', maxWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontWeight: '500', color: '#374151', fontSize: '11px' }}>
                    Duration (hrs) *
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.25"
                    placeholder="e.g. 2.5"
                    value={formData.duration_hrs}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('duration_hrs', v);
                      recalcEndFromDuration(v, formData.start_time);
                    }}
                  />
                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                    End time auto-calculates
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Remarks - one row, grows only if the planner needs it */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', color: '#374151', fontSize: '11px' }}>
              Remarks (Optional)
            </label>
            <TextArea
              placeholder="Enter any additional remarks..."
              value={formData.remarks}
              onChange={(e) => handleChange('remarks', e.target.value)}
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
          </div>

          {/* Action Buttons - stuck to the bottom of the scrolling body so they
              are reachable without hunting for them on a short screen. */}
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            paddingTop: '10px',
            marginTop: '4px',
            borderTop: '1px solid #f1f5f9',
            zIndex: 2
          }}>
            {editingActivityId && (() => {
              const isPastActivity = false;

              return !isPastActivity && (
                <Button 
                  danger
                  onClick={async () => {
                    // Find the activity being edited
                    const activityToDelete = activities.find(a => a.id === editingActivityId);
                    if (activityToDelete) {
                      setIsModalOpen(false);
                      setEditingActivityId(null);
                      await handleDeleteActivity(activityToDelete);
                    }
                  }}
                >
                  Delete
                </Button>
              );
            })()}
            <Button onClick={() => {
              setIsModalOpen(false);
              setEditingActivityId(null);
              handleReset();
            }}>
              Cancel
            </Button>
            {!editingActivityId && (
            <Button onClick={handleReset}>
              Reset
            </Button>
            )}
            {(() => {
              const isPastActivity = false;

              return (
            <Button 
              type="primary" 
              onClick={handleSubmit}
              loading={submitting}
                  disabled={isPastActivity}
              style={{ 
                    backgroundColor: isPastActivity ? '#d1d5db' : getActivityColor(formData.activity_type),
                    borderColor: isPastActivity ? '#d1d5db' : getActivityColor(formData.activity_type),
                    cursor: isPastActivity ? 'not-allowed' : 'pointer'
              }}
            >
                  {submitting 
                    ? (editingActivityId ? 'Updating...' : 'Creating...') 
                    : (editingActivityId ? 'Update Activity' : 'Create Activity')}
            </Button>
              );
            })()}
          </div>
        </div>
        </ConfigProvider>
      </Modal>

      {/* Paste confirm. Shows before -> after times for every block, and
          refuses the paste outright if anything occupies those hours. */}
      <AreaPlanPasteModal
        open={!!pasteTarget}
        planned={pasteTarget?.planned}
        targetAreaName={pasteTarget?.targetAreaName}
        targetTime={pasteTarget?.targetTime}
        onTargetTimeChange={handlePasteTimeChange}
        onConfirm={handlePasteConfirm}
        onClose={() => setPasteTarget(null)}
        pasting={pasting}
      />
    </div>
  );
};

export default AreaPlanCreate;
